<template>
  <div class="protected-page">
    <div class="lease-search-page">
      <!-- Left Column -->
      <div class="left-column">
        <!-- Property Search Bar -->
        <div class="search-bar">
          <div class="label">Search Property</div>
          <input
            ref="searchQuery"
            v-model="searchQuery"
            @input="debouncedSearch"
            type="text"
            placeholder="Search for property (use commas to refine)"
            class="search-input"
            @blur="setTempName"
          />
          <button @click="clearSearch" class="clear-button">X</button>
        </div>

        <!-- Property List -->
        <div class="content-wrapper">
          <div class="property-list-wrapper">
            <div
              v-if="properties.length && searchQuery != ''"
              class="property-list"
            >
              <div
                :title="'click to select\nthis property'"
                v-for="(property, index) in filteredProperties"
                :key="index"
                @click="selectProperty(property)"
                :class="{
                  'selected-property': property.id === selectedProperty?.id,
                }"
                class="property-item"
              >
                <span
                  v-html="highlightTerm(this.searchQuery, property.display)"
                ></span>
              </div>
            </div>
          </div>

          <!-- Lease Document List -->
          <div class="lease-doc-wrapper">
            <div v-if="selectedProperty" class="matching-leases">
              <div v-if="matchingDocuments.length" class="lease-doc-title">
                <span class="left-align">Matching lease docs:</span>
                <span
                  class="right-align"
                  style="font-weight: normal; font-size: 0.9em"
                  @click="toggleShowAllDocs"
                  >{{ showAllDocs }}</span
                >
              </div>

              <div class="doc-container" ref="docContainer">
                <div
                  v-for="(doc, index) in matchingDocuments"
                  :key="index"
                  @click="selectDocument(doc, $event)"
                  :class="{ 'selected-item': doc.Id === selectedDocument?.Id }"
                  :title="'shift-click to edit title of \n' + doc.Title"
                  @mouseover="showEye = doc.Id"
                  @mouseleave="showEye = null"
                  class="document-item"
                >
                  {{ doc.Title }}
                  <i
                    :class="doc.deleted ? 'fas fa-eye-slash' : 'fas fa-eye'"
                    title="show or hide this doc"
                    class="toggle-eye"
                    @click.stop="toggleDocumentDeleted(doc.Id, doc.deleted)"
                    v-if="showEye === doc.Id || doc.deleted"
                  ></i>
                </div>
                <div
                  class="more-arrow"
                  id="moreArrow"
                  ref="moreArrow"
                  v-show="isOverflowing"
                >
                  <i class="fas fa-chevron-down"></i>
                </div>
              </div>
              <div
                :title="'upload a new lease document.\nYou can also scan\nto email instead'"
                class="new-lease-doc"
                @click="openUploadDialog"
              >
                Add
              </div>
            </div>
          </div>
        </div>

        <!-- Lease Section -->
        <div v-if="selectedProperty" class="lease-and-payments">
          <div class="lease-section">
            <div class="label" style="margin-top: -10px">
              Leases for
              <span style="font-weight: normal">{{
                selectedProperty.display
              }}</span>
            </div>
            <div class="lease-wrapper">
              <div
                v-for="(lease, index) in selectedProperty.leases"
                :key="index"
                @click="handleLeaseClick(lease)"
                @dblclick.stop="openEditLeaseDialog(lease)"
                :class="{ 'selected-lease': lease.id === selectedLease?.id }"
                class="lease-item"
                :title="lease.name + ' (' + lease.id + ')'"
              >
                <div class="lease-info">
                  {{ formatDate(lease.startsOn) }}
                  to {{ formatDate(lease.endsOn) }} | Rent:{{
                    formatRent(lease.monthlyBaseRent)
                  }}
                  | Bal:{{ formatRent(lease.balance) }}
                </div>
                <div class="lease-icons">
                  <!-- Icons with tooltips -->
                  <i
                    @click.stop="openEditLeaseDialog(lease)"
                    class="fas fa-edit"
                    title="Edit"
                  ></i>
                  <i
                    @click.stop="
                      selectedLease = lease;
                      linkLeaseDoc(lease);
                    "
                    :class="['fas', 'fa-link', { faded: !lease.documentId }]"
                    :title="lease.documentId ? 'Unlink' : 'Link'"
                  ></i>
                  <i
                    @click.stop="
                      selectedLease = lease;
                      fetchTenants(lease.id);
                      showLedgerPanel = true;
                      showLedger(selectedProperty, lease);
                    "
                    class="fas fa-table"
                    title="Ledger"
                  ></i>
                  <i
                    @click.stop="
                      selectedLease = lease;
                      fetchTenants(lease.id);
                      showLedgerPanel = false;
                      showLedger(selectedProperty, lease);
                    "
                    class="fas fa-file-pdf"
                    title="show lease PDF"
                  ></i>
                  <i
                    @click.stop="
                      this.selectedLease = lease;
                      openNotesPanel(lease.id, 'lease');
                    "
                    class="fas fa-sticky-note"
                    title="Note"
                  ></i>
                </div>
              </div>
              <div
                :title="'Add a New lease\nentry for this address'"
                @click.stop="addnewLease()"
                class="new-lease"
              >
                Add
              </div>
            </div>
          </div>
        </div>

        <!-- Tenant Section  -->
        <div v-if="selectedLease" class="tenant-and-payments">
          <div class="label">Tenants for the selected lease:</div>
          <div class="tenant-section">
            <div
              v-for="(tenant, index) in tenants"
              :key="index"
              @click="selectTenant(tenant)"
              @dblclick="handleDblClick(tenant)"
              :class="{ 'selected-tenant': tenant.id === selectedTenant?.id }"
              class="tenant-item"
            >
              <div
                class="tenant-info"
                :class="{
                  'primary-tenant-name': Number(tenant.isPrimaryTenant) === 1,
                }"
              >
                {{ tenant.fullName }}
              </div>
              <div
                class="tenant-info"
                @click="openTenantEnvelope(tenant)"
              >
                <i
                  v-if="tenant.email"
                  class="fas fa-envelope"
                  style="margin-right: 5px"
                  :title="tenant.email"
                ></i>
                <span :title="getAllPhoneNumbers(tenant)">{{
                  getFirstPhoneNumber(tenant)
                }}</span>
              </div>
              <div class="tenant-icons">
                <!-- Icons with tooltips -->
                <i
                  @click.stop="navigatePerson(tenant)"
                  style="margin-left: 72px"
                  class="fas fa-search"
                  title="search"
                ></i>
                <i
                  @click.stop="
                    selectedTenant = tenant;
                    openPersonEditor(tenant);
                  "
                  class="fas fa-edit"
                  title="Edit"
                ></i>
                <i
                  @click.stop="
                    selectedTenant = tenant;
                    removeTenantFromLease(tenant);
                  "
                  class="fas fa-link"
                  title="Link"
                ></i>
                <i class="fas fa-folder" title="File"></i>
                <i
                  @click.stop="
                    this.selectedTenant = tenant;
                    openNotesPanel(tenant.id, 'tenant');
                  "
                  class="fas fa-sticky-note"
                  title="Note"
                ></i>
              </div>
            </div>
            <div
              :title="'Add a New tenant\nfor the selected lease'"
              @click.stop="
                showLedgerPanel = false;
                showTenantSearchDialog = true;
              "
              class="new-tenant"
            >
              Add
            </div>
          </div>
        </div>

        <!-- Tenant payment history -->
        <div v-if="selectedTenant" class="tenant-payment-history-container">
          <div style="display: flex; justify-content: space-between">
            <div class="label">
              Tenant payment history
              <small><i>double-click to link/unlink payments</i></small>
            </div>
            <button
              style="flex: 1; max-width: 35px"
              @click="captureImage('paymentHistoryTable')"
              id="captureImage"
            >
              <i class="fas fa-camera"></i>
            </button>
          </div>

          <div id="paymentHistoryTable" class="table-container">
            <table class="table">
              <thead>
                <tr>
                  <th
                    v-for="header in tph_headers"
                    :key="header"
                    :class="{
                      narrow: header === 'Date',
                      'right-align narrow': header === 'Amount',
                      'transact-num': header === 'Transact.#',
                      'center-align-narrow':
                        header === 'T' || header === 'N' || header === 'P',
                      'wide-column': header === 'Name',
                    }"
                  >
                    {{ header }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(payment, index) in tenantPayments"
                  :key="payment.id"
                  :class="{
                    'odd-row': index % 2 === 0,
                    'selected-row': selectedPaymentIds.includes(payment.id),
                    'highlighted-payment': selectedPaymentId === payment.id,
                    // || selectedLease.id === payment.leaseId,
                  }"
                  @dblclick="confirmLinkPayment(payment.id)"
                >
                  <td class="narrow" :title="payment.id">
                    {{ formatDate(payment.paidOn) }}
                  </td>
                  <td class="right-align narrow" :title="payment.name">
                    {{ formatRent(payment.amount) }}
                  </td>
                  <td class="wide-column">{{ payment.fullName }}</td>
                  <td class="transact-num" :title="payment.transactionNumber">
                    {{ payment.transactionNumber }}
                  </td>
                  <td
                    class="center-align-narrow"
                    v-html="getPaymentIcon(payment.method).html"
                    :title="getPaymentIcon(payment.method).title"
                  ></td>
                  <td
                    class="center-align-narrow"
                    v-html="getPurposeIcon(payment.purpose).html"
                    :title="getPurposeIcon(payment.purpose).title"
                  ></td>
                  <td
                    class="center-align-narrow"
                    @click="editPaymentNote(payment.id)"
                    :title="payment.note"
                  >
                    <i v-if="payment.note" class="fas fa-sticky-note"></i>
                  </td>
                </tr>
              </tbody>
            </table>
            <div style="margin: 5px"><small>end of payment history</small></div>
          </div>
        </div>
      </div>

      <!-- Right Column -->
      <div class="right-column">
        <!-- PDF Viewer -->
        <div
          v-if="selectedProperty && selectedDocument && !showLedgerPanel"
          class="pdf-viewer"
        >
          <iframe
            style="height: 780px"
            id="pdf-frame"
            :src="pdfUrl"
            frameborder="0"
          ></iframe>
        </div>

        <!-- Ledger Panel -->
        <div
          v-if="selectedProperty && selectedLease && showLedgerPanel"
          class="ledger-wrapper"
        >
          <div class="ledger-body">
            <div class="ledger-header">
              <!-- Button Group -->
              <div class="button-group">
                <button
                  title="share this"
                  @click="shareURL(this.selectedLease.id)"
                  id="shareLease"
                >
                  <i class="fas fa-share-nodes"></i>
                </button>
                <button
                  title="take a screenshot of the ledger"
                  @click="captureImage('ledgerBody')"
                  id="captureImage"
                >
                  <i class="fas fa-camera"></i>
                </button>
                <button
                  title="refresh the ledger data"
                  @click="showLedger(selectedProperty, selectedLease)"
                >
                  <i class="fas fa-refresh"></i>
                </button>
                <button
                  title="print the Ledger"
                  @click="printLedger('ledgerBody')"
                  :disabled="isLedgerPrinting"
                >
                  <i v-if="!isLedgerPrinting" class="fas fa-print"></i>
                  <span v-if="isLedgerPrinting" class="spinner"></span>
                </button>
                <button
                  title="email ledger to tenants"
                  @click="captureImageforEmail"
                >
                  <i class="fas fa-envelope"></i>
                </button>
                <button
                  title="add a debit or credit"
                  @click="
                    this.addNewLedgerEntry = true;
                    setFormType('add');
                  "
                >
                  <i class="fas fa-plus"></i>
                </button>
                <button
                  title="show help"
                  @click="
                    this.showHelp = !this.showHelp;
                    this.ledgerHelp = true;
                  "
                >
                  <i class="fas fa-question"></i>
                </button>
                <button
                  :title="'make list of \nledgers (for DL)'"
                  @click="openLedgerPopup()"
                >
                  <i class="fas fa-list"></i>
                </button>
              </div>
              <span v-if="selectedLedgerRows.size > 0" class="selected-total">
                Selected: {{ formattedSelectedTotal }}
              </span>
            </div>

            <!-- Ledger Summary Section -->
            <div id="ledgerBody">
              <div class="ledger-summary">
                <div
                  class="ledger-title"
                  style="
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                  "
                >
                  <span style="text-align: left"
                    >Ledger for {{ selectedProperty.display }}</span
                  >
                  <span
                    :style="{
                      background:
                        selectedLease.balance == 0
                          ? '#aaa'
                          : selectedLease.balance < 0
                            ? '#955858'
                            : 'green',
                      color: 'white',
                      textAlign: 'right',
                      padding: '4px',
                    }"
                  >
                    {{ balanceMessage }}
                  </span>
                </div>

                <!-- first Row -->
                <div class="summary-row full-width">
                  <div class="summary-item">
                    <label>Tenant(s):</label>
                    <strong>{{ ledgerSummary.tenant }}</strong>
                  </div>
                </div>
                <!-- second Row -->
                <div class="summary-row">
                  <div class="summary-item">
                    <strong>{{ ledgerSummary.property }}</strong>
                  </div>
                  <div class="summary-item">
                    <label>from:</label>
                    <strong>{{ formatDate(ledgerSummary.startsOn) }}</strong>
                  </div>
                  <div class="summary-item">
                    <label>to:</label>
                    <strong>{{ formatDate(ledgerSummary.endsOn) }}</strong>
                  </div>
                  <div class="summary-item" style="justify-content: right">
                    <label>rent:</label>
                    <strong>{{
                      formatRent(ledgerSummary.monthlyBaseRent)
                    }}</strong>
                  </div>
                </div>

                <!-- Third Row -->
                <div class="summary-row">
                  <div class="summary-item">
                    <label>Sec. Deposit:</label>
                    <strong>{{
                      formatRent(ledgerSummary.securityDeposit)
                    }}</strong>
                    <label>&nbsp;paid on:</label>
                    <strong>{{
                      ledgerSummary.paidOn
                        ? formatDate(ledgerSummary.paidOn)
                        : ''
                    }}</strong>
                  </div>
                  <div class="summary-item">
                    <a href="#">{{ ledgerSummary.paymentRef }}</a>
                  </div>
                  <div class="summary-item" style="justify-content: right">
                    <label>prorated:</label>
                    <strong>{{ formatRent(ledgerSummary.prorated) }}</strong>
                  </div>
                </div>

                <!-- Fourth Row - Additional Services -->
                <div
                  class="summary-row"
                  v-if="
                    selectedLease.hasParking ||
                    selectedLease.hasStorage ||
                    selectedLease.hasMobileAC
                  "
                >
                  <div class="summary-item full-width" style="font-size: 0.9em">
                    <span v-if="selectedLease.hasParking">
                      <label>parking:</label>
                      <strong>{{ selectedLease.parkingSpot || 'N/A' }}</strong>
                      <label>&nbsp;Fee:</label>
                      <strong>{{
                        formatRent(selectedLease.parkingFee)
                      }}</strong>
                    </span>
                    <span
                      v-if="selectedLease.hasStorage"
                      :style="{
                        marginLeft: selectedLease.hasParking ? '15px' : '0',
                      }"
                    >
                      <label>storage:</label>
                      <strong>{{ selectedLease.storageDesc || 'N/A' }}</strong>
                      <label>&nbsp;Fee:</label>
                      <strong>{{
                        formatRent(selectedLease.storageFee)
                      }}</strong>
                    </span>
                    <span
                      v-if="selectedLease.hasMobileAC"
                      :style="{
                        marginLeft:
                          selectedLease.hasParking || selectedLease.hasStorage
                            ? '15px'
                            : '0',
                      }"
                    >
                      <label>windowAC:</label>
                      <strong>{{
                        selectedLease.mobileACModel || 'N/A'
                      }}</strong>
                      <label>&nbsp;Fee:</label>
                      <strong>{{
                        formatRent(selectedLease.mobileACRent)
                      }}</strong>
                    </span>
                  </div>
                </div>
              </div>

              <!-- Ledger Table Section -->
              <div
                class="transaction-table-outer"
                @mouseenter="isMouseOverLedger = true"
                @mouseleave="isMouseOverLedger = false"
              >
                <div class="transaction-table-inner">
                  <table class="transaction-table">
                    <thead>
                      <tr>
                        <th
                          v-for="(header, index) in ledgerHeaders"
                          :key="index"
                          :class="getColumnClass(index)"
                        >
                          {{ header }}
                          <!-- Display totalCredit for colIndex 2 and totalDebit for colIndex 3 -->
                          <div v-if="index === 2">
                            {{ formattedTotalCredit }}
                          </div>
                          <div v-if="index === 3">
                            {{ formattedTotalDebit }}
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody v-if="selectedLease.startsOn">
                      <tr
                        v-for="(rowData, rowIndex) in filteredLedgerData"
                        :key="rowIndex"
                        @click="handleLedgerRowClick($event, rowIndex)"
                        @dblclick="editLedgerRow(rowIndex)"
                        :class="{
                          'selected-row': rowIndex === selectedLedgerRow,
                          'multi-selected-row':
                            selectedLedgerRows.has(rowIndex),
                        }"
                      >
                        <td
                          v-for="(value, colIndex) in rowData.values"
                          :key="colIndex"
                          :class="getColumnClass(colIndex)"
                        >
                          <div v-if="colIndex === 0">
                            {{ formatDate(value) }}
                          </div>
                          <div
                            v-else-if="colIndex === 1"
                            :title="rowData.originalRow.account || ''"
                            :style="
                              rowData.originalRow.account
                                ? 'font-weight: bold'
                                : ''
                            "
                          >
                            {{ value }}
                          </div>
                          <div
                            v-else-if="colIndex === 4"
                            style="text-align: center; width: 120px"
                          >
                            {{ value }}
                          </div>
                          <div
                            v-else-if="colIndex === 5"
                            style="text-align: center"
                          >
                            <!-- Grp column - show icon if this is a payment (but not Conv. fee) -->
                            <i
                              v-if="
                                rowData.originalRow.paymentId &&
                                rowData.originalRow.depositGroup &&
                                rowData.originalRow.description !== 'Conv. fee'
                              "
                              class="fas fa-check"
                              style="color: green"
                              :title="rowData.originalRow.depositGroup"
                            ></i>
                            <i
                              v-else-if="
                                rowData.originalRow.paymentId &&
                                !rowData.originalRow.depositGroup &&
                                isDaysOld(rowData.originalRow.paidOn, 14) &&
                                rowData.originalRow.description !== 'Conv. fee'
                              "
                              class="fas fa-times"
                              style="color: red"
                              title="No deposit group"
                            ></i>
                            <i
                              v-else-if="
                                rowData.originalRow.paymentId &&
                                !rowData.originalRow.depositGroup &&
                                rowData.originalRow.description !== 'Conv. fee'
                              "
                              class="fas fa-question"
                              style="color: orange"
                              title="No deposit group"
                            ></i>
                          </div>
                          <div v-else-if="colIndex === 6" style="width: 50px">
                            {{ value }}
                          </div>
                          <div v-else-if="colIndex === 2">
                            {{
                              value === '0.00' || value === 0
                                ? ''
                                : formatRent(value)
                            }}
                          </div>
                          <div v-else-if="colIndex === 3">
                            {{
                              value === '0.00' || value === 0
                                ? ''
                                : formatRent(value)
                            }}
                          </div>
                          <div v-else-if="colIndex >= 7 && colIndex <= 9">
                            {{ formatRent(value) }}
                          </div>

                          <!-- Display note icon with tooltip for colIndex 10 -->
                          <div v-else-if="colIndex === 10">
                            <i
                              v-if="value"
                              class="fas fa-sticky-note"
                              :title="value"
                            ></i>
                          </div>
                          <div
                            v-else-if="colIndex === 11"
                            @click="selectCheck(value)"
                          >
                            <i
                              v-if="value"
                              class="fas fa-money-check"
                              :title="value"
                            ></i>
                          </div>

                          <!-- Default rendering for other values -->
                          <div v-else>{{ value }}</div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <div class="ledger-howto">
                    How to read this ledger: Your net balance adjusts on the
                    first of each month by deducting your rent due amount;
                    "Expected Rent" rows shows the dates when this occurs.
                    Your payments are added to your net balance to bring it
                    toward zero. A negative net balance means you owe money; a
                    positive net balance means you have a net credit amount.<br /><br />Ideally,
                    each month your balance should be zero after your payments
                    are applied. If you have unpaid amounts carried forward, or
                    have late or other fees assessed, your balance will show as
                    a negative number to reflect this. You should pay this
                    remaining balances as soon as possible, but no later than
                    with your next rent payment. If you have co-tenants,
                    payments will show the initials of the payees to help you
                    reconcile your individual payments.
                  </div>
                  <div class="no-start-date" v-if="!selectedLease.startsOn">
                    no valid start date for lease
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="selectedProperty && selectedLease && showLedgerPanel"
        class="temp-column"
      >
        <div v-if="showQuickBooksTool" class="temp-data container">
          <div>
            <div class="temp-data-title">Quickbooks Lookup Tool</div>
            <div class="tip">
              Enter address with no Apt e.g. use '#5' then press TAB
            </div>
            <div class="temp-form">
              <input
                id="namePattern"
                v-model="namePattern"
                type="text"
                placeholder="Enter name pattern (e.g., Eastern)"
                @blur="fetchTempData"
              />
              <input
                id="afterDate"
                v-model="afterDate"
                type="date"
                @blur="fetchTempData"
              />
            </div>
            <div class="table">
              <div class="temp-row temp-header">
                <div class="temp-cell" style="width: 120px">Date</div>
                <div style="width: 60px; text-align: left" class="temp-cell">
                  Type
                </div>
                <div style="width: 60px; text-align: right" class="temp-cell">
                  Amount
                </div>
                <div style="width: 80px" class="temp-cell">Num</div>
                <div style="width: 90px" class="temp-cell">Lease</div>
              </div>
            </div>
            <div class="temp-table-wrapper">
              <div v-if="tempData.length > 0" class="table-body">
                <div
                  v-for="(row, index) in filteredTempData"
                  :key="index"
                  class="temp-row"
                  :class="{ highlight: selectedTempRow === index }"
                  @click="selectTempRow(index, row, $event)"
                  @dblclick="syncTempDataToLedger(row)"
                >
                  <div
                    style="width: 120px"
                    class="temp-cell"
                    :title="splitName(row.Name)"
                  >
                    {{ formatDate(row.Date) }}
                  </div>
                  <div style="width: 60px; text-align: left" class="temp-cell">
                    {{ row.Type }}
                  </div>
                  <div
                    :title="row.Split"
                    style="width: 60px; text-align: right"
                    class="temp-cell"
                  >
                    {{ parseFloat(row.Amount).toFixed(2) }}
                  </div>
                  <div
                    style="padding-left: 20px; width: 80px; text-align: center"
                    class="temp-cell"
                  >
                    {{ row.Num }}
                  </div>
                  <div
                    style="width: 90px; text-align: left"
                    class="temp-cell"
                    :title="row.Name"
                  >
                    {{ splitName(row.Name) }}
                  </div>
                </div>
              </div>
              <div v-else class="temp-row">
                <div class="temp-cell" colspan="5">No records found.</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Overlay for the dialog -->
    <div v-if="showUploadDialog" class="upload-dialog-overlay"></div>

    <!-- Confirmation Dialog -->
    <div v-if="showDialogPopup" class="confirmation-popup-content">
      <div class="confirmation-popup-title">Confirmation needed</div>
      <div class="confirmation-message" v-html="dialogMessage"></div>
      <div class="confirmation-popup-buttons">
        <button
          v-for="option in dialogOptions"
          :key="option"
          @click="onDialogOptionSelected(option)"
          :id="option === 'Confirm' ? 'confirmButton' : ''"
        >
          {{ option }}
        </button>
      </div>
    </div>

    <!-- Payment Note Dialog -->
    <div v-if="showPaymentNoteDialog" class="edit-dialog">
      <div class="edit-dialog-content">
        <h2>Edit Payment Note</h2>
        <textarea v-model="paymentNote" class="note-textarea"></textarea>
        <div class="edit-dialog-footer">
          <button @click="savePaymentNote">Save</button>
          <button @click="showPaymentNoteDialog = false">Cancel</button>
        </div>
      </div>
    </div>

    <!-- Ledger Popup for Editing/Adding Entries -->
    <div id="ledgerPopup" v-if="showFormCondition" class="ledger-popup">
      <div class="ledger-popup-content">
        <div class="popup-header">
          <span>{{ formTitle }}</span>
          <button
            style="background: none"
            class="close-button"
            @click="closeLedgerForm"
          >
            <i class="fas fa-close"></i>
          </button>
        </div>

        <div v-if="!addNewLedgerEntry" class="button-row">
          <button
            class="popup-button"
            @click="setFormType('edit')"
            :class="{ active: formMode === 'edit' }"
          >
            <i class="fas fa-edit"></i>
            <div>Edit</div>
          </button>
          <button
            class="popup-button"
            @click="
              this.addNewLedgerEntry = true;
              setFormType('add');
            "
            :class="{ active: formMode === 'add' }"
          >
            <i class="fas fa-plus"></i>
            <div>Add</div>
          </button>
          <button
            class="popup-button"
            @click="confirmDeleteLedgerEntry"
            :class="{ active: formMode === 'delete' }"
          >
            <i class="fas fa-trash"></i>
            <div>Delete</div>
          </button>
          <button
            v-if="isLeasePaymentRecord"
            class="popup-button"
            @click="rejectPayment"
            title="Add an offsetting debit for a rejected payment"
          >
            <i class="fas fa-ban"></i>
            <div>Reject</div>
          </button>
        </div>

        <!-- Form for Editing or Adding Lease Ledger or Lease Payment Records -->
        <div class="form" v-if="showSubForm">
          <div v-if="isLeaseLedgerRecord">
            <!-- Fields for Lease Ledger Records -->

            <div class="form-row">
              <label>Date</label>
              <input ref="editLedgerDate" v-model="form.date" type="date" />
            </div>

            <div class="form-row">
              <label for="description">Description</label>

              <!-- Dropdown for predefined inputs -->
              <select
                v-model="selectedPredefined"
                @blur="updateDescription"
                @change="updateDescription"
              >
                <option value="" disabled>Select a description</option>
                <option
                  v-for="(option, index) in predefinedOptions"
                  :key="index"
                  :value="option"
                >
                  {{ option }}
                </option>
              </select>
            </div>

            <!-- Conditional rendering of input field -->
            <div class="form-row" v-if="selectedPredefined === 'Other...'">
              <label for="description">Description</label>
              <input
                ref="ledgerFormDescription"
                v-model="form.description"
                placeholder="Enter your description"
                type="text"
              />
            </div>

            <div class="form-row">
              <label>Credit</label>
              <input v-model="form.credit" placeholder="0.00" type="number" />
            </div>

            <div class="form-row">
              <label>Debit</label>
              <input v-model="form.debit" placeholder="0.00" type="number" />
            </div>

            <div class="form-row">
              <label>Transaction No. </label>
              <input
                v-model="form.num"
                placeholder="transaction number"
                type="text"
              />
            </div>

            <div class="form-row">
              <label>Type</label>
              <select v-model="form.type">
                <option value="x">Choose...</option>
                <option value="0">Other</option>
                <option value="1">Fee</option>
                <option value="2">Credit</option>
                <option value="3">Refund</option>
                <option value="4">Writeoff</option>
              </select>
            </div>

            <div class="form-row">
              <label>Account</label>
              <select v-model="form.account">
                <option :value="null"></option>
                <option
                  v-for="acct in accountOptions"
                  :key="acct.id"
                  :value="acct.value"
                >
                  {{ acct.label }}
                </option>
              </select>
            </div>
          </div>

          <div v-if="isLeasePaymentRecord">
            <!-- Fields for Lease Payment Records -->

            <div class="form-row">
              <label>Date</label>
              <input v-model="form.date" type="date" />
            </div>

            <div class="form-row">
              <label>Amount</label>
              <input v-model="form.credit" type="number" />
            </div>

            <div class="form-row">
              <label>Transaction Number</label>
              <input v-model="form.num" type="text" />
            </div>

            <div class="form-row">
              <label>Method</label>
              <select v-model="form.method">
                <option value="1">Check</option>
                <option value="2">E-check</option>
                <option value="3">Cashier's Check</option>
                <option value="4">Other</option>
                <option value="6">Billpayment Svc</option>
              </select>
            </div>

            <div class="form-row">
              <label>Purpose</label>
              <select v-model="form.purpose">
                <option value="0">Unknown</option>
                <option value="1">Rent</option>
                <option value="2">Prorated</option>
                <option value="3">Fees</option>
                <option value="4">Security Deposit</option>
              </select>
            </div>
          </div>

          <div class="form-actions">
            <div class="popup-footer" v-if="showSubForm">
              <button @click="submitLedgerForm">Save Changes</button>
              <button @click="closeLedgerForm">Close</button>
            </div>
          </div>
        </div>
        <div
          :class="form"
          v-if="!showSubForm && showEffectiveDate && isLeasePaymentRecord"
        >
          <div class="form-row">
            <label>Effective Date</label>
            <input v-model="form.date" type="date" />
          </div>
          <div class="popup-footer" v-if="showEffectiveDate">
            <button @click="submitEffectiveDate">Save Changes</button>
            <button @click="closeLedgerForm">Close</button>
          </div>
        </div>
        <div class="new-ledger-entry-error">{{ ledgerErrorMessage }}</div>
      </div>
    </div>

    <!-- Background overlay -->
    <div v-if="showEmailComposer" class="email-compose-wrapper-overlay"></div>

    <!-- Email Composition Section (Popup) -->
    <div
      class="email-compose-wrapper"
      v-if="showEmailComposer"
      ref="emailComposer"
    >
      <!-- Close Button -->
      <button class="email-compose-close-btn" @click="closeComposer">×</button>

      <!-- Popup Header -->
      <div
        class="email-compose-header"
        @mousedown.stop.prevent="startPopupDrag($event, 'emailComposer')"
      >
        <span>Compose Email</span>
      </div>

      <!-- Popup Body -->
      <div class="email-compose-body">
        <!-- Recipients Email (To:) -->
        <div class="email-compose-row">
          <label for="to">To:</label>
          <input
            type="text"
            id="to"
            :value="recipients"
            readonly
            placeholder="Email recipients"
          />
          <button
            class="preview-button"
            @click="printComposeEmailPreview"
            title="Print preview of current draft"
          >
            <i class="fas fa-print"></i> Preview
          </button>
          <button
            class="preview-button dan-to-button"
            @click="setToDan"
            title="Set To: dan@brandenburgcapitalmanagement.com"
          >
            <i class="fas fa-envelope"></i> Dan
          </button>
        </div>
        <!-- Subject Input -->
        <div class="email-compose-row">
          <label for="subject">Subject:</label>
          <input
            type="text"
            id="subject"
            v-model="subject"
            placeholder="Enter subject"
          />
        </div>
        <div>
          <!-- row of buttons -->
          <button
            class="quick-action"
            @click="insertQuickAction('rentReminder')"
          >
            Rent Reminder
          </button>
          <button class="quick-action" @click="insertQuickAction('balanceDue')">
            Balance Due
          </button>
          <button
            class="quick-action"
            @click="insertQuickAction('information')"
          >
            Information
          </button>
        </div>

        <!-- Text Area for Adding Text Above the Image -->
        <textarea
          id="emailBodyTop"
          ref="emailBodyTop"
          v-model="emailBodyTop"
          placeholder="Email body (can use markdown)"
          @input="updatePreview"
        ></textarea>

        <!-- Text Area for Adding Text Below the Image -->
        <textarea
          id="emailBodyBottom"
          @input="updatePreview"
          v-model="emailBodyBottom"
          placeholder="Email text below the image"
        ></textarea>
      </div>
      <div class="preview-label">
        Email body preview
        <span class="markdown-help">
          <a href="https://www.markdownguide.org/cheat-sheet/" target="_blank"
            >Markdown guide</a
          >
        </span>
      </div>
      <div class="compose-preview" v-html="composeEmailPreview"></div>

      <!-- Popup Footer -->
      <div class="email-compose-footer">
        <button class="email-compose-btn-save" @click="sendEmail">Send</button>
        <button class="email-compose-btn-close" @click="closeComposer">
          Close
        </button>
      </div>
    </div>

    <!-- End of lease-search-page -->

    <div
      v-if="showHelp"
      @dblclick="
        this.showHelp = false;
        this.ledgerHelp = false;
      "
      class="help-popup"
    >
      <div v-if="ledgerHelp">
        <strong>You can do the following things with the ledger:</strong>
        <ul>
          <li>Add ledger entries (fees, credits) using the [+] button.</li>
          <li>
            double click payment entries to set the effective date. You would do
            this if a payment was received before the start of the lease, for
            example, to make it co-incident with, or after, the start of the
            lease.
          </li>
          <li>double click ledger entries (fees, credits) to modify them.</li>
          <li>
            click the camera button to copy a screenshot of the ledger to the
            clipboard to paste elsewhere
          </li>
          <li>
            click the email button to send a copy of the ledger to the tenants,
            with a message attached
          </li>
          <li>print the ledger, using the printer button.</li>
          <li>refresh the ledger with the refresh button</li>
        </ul>
      </div>
      <div class="close-help">double click this popup to close.</div>
    </div>

    <!-- PDF viewer-->
    <div
      v-if="pdfUrl && showPDF"
      :class="[
        'draggable-popup',
        viewerTitle === 'Check Viewer' ? 'check-viewer' : 'ledger-viewer',
      ]"
      ref="pdfViewer"
    >
      <div class="check-viewer-header">
        <div
          class="check-viewer-title"
          @click="bringToFront($event)"
          @mousedown.stop.prevent="startPopupDrag($event, 'pdfViewer')"
        >
          {{ viewerTitle }}
          <!-- Draggable Icon -->
          <i
            class="fas fa-crosshairs draggable-icon"
            :class="{ active: isDragMode }"
            ref="ledgerListPopupIcon"
            @click.stop="toggleDragMode"
          ></i>
        </div>
        <button class="check-viewer-close-button" @click="showPDF = false">
          X
        </button>
      </div>
      <iframe id="pdf-frame" :src="pdfUrl" frameborder="0"></iframe>
    </div>

    <div
      v-if="showLedgerListPopup"
      ref="ledgerListPopup"
      class="ledger-list-popup draggable-popup"
    >
      <div
        class="ledger-list-title"
        @click="bringToFront($event)"
        @mousedown.stop.prevent="startPopupDrag($event, 'ledgerListPopup')"
      >
        Ledger Watch list: for email attachments ({{ ledgerList.length }})
        <!-- Draggable Icon -->
        <i
          class="fas fa-crosshairs draggable-icon"
          :class="{ active: isDragMode }"
          ref="ledgerListPopupIcon"
          @click.stop="toggleDragMode"
        ></i>
      </div>

      <div class="ll-list-wrapper">
        <ul class="ledger-list">
          <li v-for="item in ledgerList" :key="item.id">
            <span @click="listClickSearch(item)">{{ item.docTitle }}</span>
            <span>{{ new Date(item.createdOn).toLocaleString() }}</span>
            <span>${{ formatBalanceOverride(item.balanceOverride) }}</span>

            <button title="replace this ledger" @click="replaceItem(item)">
              <i class="fas fa-recycle"></i>
            </button>

            <button
              title="view this ledger"
              @click="viewLedgerItem(item.id, item.docTitle)"
            >
              <i class="fas fa-eye"></i>
            </button>

            <button
              title="delete this ledger"
              @click="removeLedgerItem(item.id)"
            >
              <i class="fas fa-trash"></i>
            </button>
          </li>
        </ul>
      </div>

      <div class="ll-button-row">
        <label style="font-size: 8pt">Optional Bal Override: </label>
        <input
          class="demand-balance-override"
          v-model="demandBalanceOverride"
          @blur="evaluateDemandBalanceExpression"
          placeholder="e.g. 2080-120-420"
        />
        <button :disabled="isAdding" @click="addToList">Add This Ledger</button>
        <button @click="closeLedgerList">Close</button>
      </div>
    </div>

    <!-- Lease Edit Dialog -->
    <div v-if="showEditLeaseDialog" class="edit-dialog draggable-popup">
      <div class="edit-dialog-content">
        <div class="edit-lease-title">
          Edit or Create Lease
          <!-- Draggable Icon -->
          <i
            class="fas fa-crosshairs draggable-icon"
            :class="{ active: isDragMode }"
            ref="ledgerListPopupIcon"
            @click.stop="toggleDragMode"
          ></i>
        </div>
        <div class="edit-dialog-body">
          <!-- Add an input for the name -->
          <div class="name-input">
            <label for="editName">Name: (optional)</label>
            <input
              :title="'example: 1234 Anywhere #2 MMDDYY-MMDDYY Smith'"
              type="text"
              id="editName"
              v-model="editedLease.name"
            />
            <button
              :title="'use the document\nfilename to name \nthis lease'"
              @click="setNewLeaseDefaults"
            >
              Default
            </button>
          </div>
          <!-- Row 1 ----------------------------------------------------------- -->
          <div class="date-inputs">
            <div class="date-field">
              <label for="editStartDate">Start Date:</label>
              <input
                type="date"
                id="editStartDate"
                ref="editStartDate"
                v-model="editedLease.startsOnRaw"
                @blur="updateChecklistDate"
              />
            </div>
            <div class="date-field">
              <label for="editEndDate">End Date:</label>
              <input
                type="date"
                id="editEndDate"
                v-model="editedLease.endsOnRaw"
              />
            </div>
            <div
              class="date-field"
              :title="'when checklist returned. \ntypically a few days from \nthe start date'"
            >
              <label for="editChecklistDate">Checklist Date:</label>
              <!-- New field added -->
              <input
                type="date"
                id="editChecklistDate"
                v-model="editedLease.checklistRaw"
              />
            </div>
            <div class="amount-field"></div>
            <!-- blank cell -->
          </div>

          <!-- Row 2 ----------------------------------------------------------- -->
          <div class="amount-inputs">
            <div class="amount-field">
              <label for="editTotalLeasePayment">Total Lease:</label>
              <input
                id="editTotalLeasePayment"
                ref="editTotalLeasePayment"
                v-model="editedLease.totalLeasePayment"
                type="text"
                placeholder="0,000.00"
              />
            </div>
            <div class="amount-field">
              <label for="editProrated">Prorated:</label>
              <input
                id="editProrated"
                v-model="editedLease.prorated"
                type="text"
                placeholder="0,000.00"
              />
            </div>
            <div class="amount-field">
              <label for="editRent">Rent:</label>
              <input
                id="editRent"
                v-model="editedLease.monthlyBaseRent"
                type="text"
                placeholder="0,000.00"
              />
            </div>
            <div class="amount-field"></div>
          </div>
          <!-- Row 3 ----------------------------------------------------------- -->
          <div class="amount-inputs">
            <div class="amount-field">
              <label for="editSecurityDeposit">Sec. Dep.</label>
              <input
                id="editSecurityDeposit"
                v-model="editedLease.securityDeposit"
                type="text"
                placeholder="0,000.00"
              />
            </div>
            <div
              class="date-field"
              :title="'typically the same \nas the start date'"
            >
              <label for="editSecDepPaid">SecDep Date:</label>
              <input
                type="date"
                id="editsecDepPaidOn"
                v-model="editedLease.secDepPaidOnRaw"
              />
            </div>

            <div class="amount-field">
              <label for="editPetFee">Pet Fee:</label>
              <input
                id="editPetFee"
                v-model="editedLease.petFee"
                type="text"
                placeholder="0,000.00"
              />
            </div>
            <div class="amount-field">
              <label for="editPetCount">Pet Count:</label>
              <input
                id="editPetCount"
                v-model="editedLease.petCount"
                type="number"
                min="1"
                max="5"
              />
            </div>
          </div>

          <div style="font-size: 0.85em; margin-bottom: 8px">
            Check if landlord pays (included in lease)
          </div>
          <!-- Row 4 ----------------------------------------------------------- -->
          <div class="checkbox-group">
            <div class="checkbox-field">
              <input
                type="checkbox"
                id="includesElectric"
                v-model="editedLease.includesElectric"
              />
              <label for="includesElectric">Electricity</label>
            </div>
            <div class="checkbox-field">
              <input
                type="checkbox"
                id="includesLawncare"
                v-model="editedLease.includesLawncare"
              />
              <label for="includesLawncare">Lawncare</label>
            </div>
            <div class="checkbox-field">
              <input
                type="checkbox"
                id="earlyResign"
                v-model="editedLease.earlyResign"
              />
              <label for="earlyResign">Early re-sign</label>
            </div>
            <div class="checkbox-field">
              <input
                type="checkbox"
                id="includesTrash"
                v-model="editedLease.includesTrash"
              />
              <label for="includesTrash">Trash</label>
            </div>
            <div class="checkbox-field">
              <input
                type="checkbox"
                id="includesParkingSnowRemoval"
                v-model="editedLease.includesParkingSnowRemoval"
              />
              <label for="includesParkingSnowRemoval">Parking Snow Rem.</label>
            </div>
            <div class="checkbox-field">
              <input
                type="checkbox"
                id="includesCableTV"
                v-model="editedLease.includesCableTV"
              />
              <label for="includesCableTV">Cable TV</label>
            </div>
            <div class="checkbox-field">
              <input
                type="checkbox"
                id="includesGas"
                v-model="editedLease.includesGas"
              />
              <label for="includesGas">Gas</label>
            </div>
            <div class="checkbox-field">
              <input
                type="checkbox"
                id="includesWalkSnowRemoval"
                v-model="editedLease.includesWalkSnowRemoval"
              />
              <label for="includesWalkSnowRemoval">Walk Snow Rem.</label>
            </div>
            <div class="checkbox-field">
              <input
                type="checkbox"
                id="includesPhone"
                v-model="editedLease.includesPhone"
              />
              <label for="includesPhone">Phone</label>
            </div>
            <div class="checkbox-field">
              <input
                type="checkbox"
                id="includesWaterSewer"
                v-model="editedLease.includesWaterSewer"
              />
              <label for="includesWaterSewer">Water/Sewer</label>
            </div>
          </div>

          <!-- Row 6 - Additional Services ---------------------------------------- -->
          <div style="font-size: 0.85em; margin-bottom: 6px; margin-top: 10px">
            Additional Services
          </div>
          <div class="additional-services-group">
            <!-- Parking Row -->
            <div class="service-row">
              <div class="checkbox-field">
                <input
                  type="checkbox"
                  id="hasParking"
                  v-model="editedLease.hasParking"
                />
                <label for="hasParking">Parking</label>
              </div>
              <input
                type="text"
                v-model="editedLease.parkingSpot"
                placeholder="Spot #"
                class="service-input"
              />
              <input
                type="text"
                v-model="editedLease.parkingFee"
                placeholder="Fee ($)"
                class="service-input"
                @blur="onParkingFeeBlur"
              />
              <input
                type="date"
                v-model="editedLease.parkingStartRaw"
                title="Parking fee start date"
                class="service-input service-date-input"
              />
            </div>

            <!-- Storage Row -->
            <div class="service-row">
              <div class="checkbox-field">
                <input
                  type="checkbox"
                  id="hasStorage"
                  v-model="editedLease.hasStorage"
                />
                <label for="hasStorage">Storage</label>
              </div>
              <input
                type="text"
                v-model="editedLease.storageDesc"
                placeholder="Description"
                class="service-input"
              />
              <input
                type="text"
                v-model="editedLease.storageFee"
                placeholder="Fee ($)"
                class="service-input"
                @blur="onStorageFeeBlur"
              />
              <input
                type="date"
                v-model="editedLease.storageStartRaw"
                title="Storage fee start date"
                class="service-input service-date-input"
              />
            </div>

            <!-- Mobile A/C Row -->
            <div class="service-row">
              <div class="checkbox-field">
                <input
                  type="checkbox"
                  id="hasMobileAC"
                  v-model="editedLease.hasMobileAC"
                />
                <label for="hasMobileAC">Mobile A/C</label>
              </div>
              <input
                type="text"
                v-model="editedLease.mobileACModel"
                placeholder="Model"
                class="service-input"
              />
              <input
                type="text"
                v-model="editedLease.mobileACRent"
                placeholder="Rent ($)"
                class="service-input"
                @blur="onMobileACRentBlur"
              />
              <input
                type="date"
                v-model="editedLease.mobileACStartRaw"
                title="Mobile A/C rent start date"
                class="service-input service-date-input"
              />
            </div>
          </div>

          <!-- Row 6b - Pet Info ------------------------------------------------- -->
          <div style="font-size: 0.85em; margin-bottom: 6px; margin-top: 10px">
            Pet Info
          </div>
          <div class="pet-info-row">
            <div class="pet-field">
              <label>Dog:</label>
              <input
                type="number"
                min="0"
                max="9"
                v-model.number="editedLease.dogCount"
                class="pet-count-input"
              />
            </div>
            <div class="pet-field">
              <label>Cat:</label>
              <input
                type="number"
                min="0"
                max="9"
                v-model.number="editedLease.catCount"
                class="pet-count-input"
              />
            </div>
            <div class="pet-field pet-names-field">
              <label>Names:</label>
              <input
                type="text"
                v-model="editedLease.petNames"
                placeholder="e.g. Fido, Fluffy"
                class="pet-names-input"
              />
            </div>
            <button
              type="button"
              class="pet-temperament-btn"
              :style="{
                backgroundColor: temperamentColor(editedLease.dogTemperament),
              }"
              @click="cycleTemperament('dogTemperament')"
              :title="temperamentLabel(editedLease.dogTemperament)"
            >
              <i class="fas fa-dog"></i>
            </button>
            <button
              type="button"
              class="pet-temperament-btn"
              :style="{
                backgroundColor: temperamentColor(editedLease.catTemperament),
              }"
              @click="cycleTemperament('catTemperament')"
              :title="temperamentLabel(editedLease.catTemperament)"
            >
              <i class="fas fa-cat"></i>
            </button>
          </div>
        </div>
        <!-- Row 7 ----------------------------------------------------------- -->
        <div class="date-inputs">
          <div class="date-field">
            <label for="editSignedOn">Signed On:</label>
            <input
              type="date"
              id="editSignedOn"
              v-model="editedLease.signedOnRaw"
            />
          </div>
        </div>
        <div class="validation-message">
          {{ validationMessage }}
        </div>
        <div class="edit-dialog-footer">
          <button @click="saveLeaseEdits">Save</button>
          <button @click="closeEditLeaseDialog">Cancel</button>
          <button @click="deleteLease">Delete</button>
        </div>
      </div>
    </div>
    <!-- Tenant Search Dialog (select existing people to link to the lease) -->
    <div
      v-if="showTenantSearchDialog"
      class="tenant-search-dialog draggable-popup"
    >
      <div class="edit-dialog draggable-popup">
        <div class="edit-dialog-content">
          <div class="edit-dialog-title">
            Add Tenant to Lease

            <i
              style="position: absolute; right: 35px; top: 0px"
              class="fas fa-close"
              @click.stop="closeTenantSearchDialog"
            ></i>
            <!-- Draggable Icon -->
            <i
              class="fas fa-crosshairs draggable-icon"
              :class="{ active: isDragMode }"
              ref="ledgerListPopupIcon"
              @click.stop="toggleDragMode"
            ></i>
          </div>
          <div class="edit-dialog-body">
            <input
              type="text"
              ref="tenantSearchQuery"
              v-model="tenantSearchQuery"
              @input="debouncedTenantSearch"
              placeholder="Search tenants..."
              class="search-input"
            />
            <span style="margin-left: 5px"
              ><small>(click: select; double-click: edit)</small></span
            >
            <div v-if="tenantSearchResults.length" class="search-results">
              <ul style="list-style-type: none; margin-left: -39px">
                <li
                  v-for="person in tenantSearchResults"
                  :key="person.id + '-' + person.leaseId"
                  @click="handleClick(person)"
                  @dblclick="handleDblClick(person)"
                  :class="{
                    'selected-item':
                      selectedPerson && selectedPerson.id === person.id,
                  }"
                >
                  <span
                    v-html="
                      highlightTerm(
                        this.tenantSearchQuery,
                        person.fullName + ' ',
                      )
                    "
                  ></span>
                  <span
                    v-html="
                      highlightTerm(
                        this.tenantSearchQuery,
                        person.phone ? person.phone + ' ' : ' ',
                      )
                    "
                  ></span>
                  <span
                    v-html="
                      highlightTerm(
                        this.tenantSearchQuery,
                        person.email ? person.email + ' ' : ' ',
                      )
                    "
                  ></span>
                  <span>{{ person.active === 1 ? '(active)' : '' }}</span>
                </li>
              </ul>
            </div>
            <div v-else class="search-results">No results found.</div>
          </div>
          <div class="edit-dialog-footer">
            <button @click="openPersonEditor(null)">New Contact</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Importing NotesPanel -->
    <NotesPanel
      :showNotesPanel="this.showNotesPanel"
      :noteType="this.noteType"
      :noteId="this.noteId"
      @update-show-notes-panel="updateShowNotesPanel"
    />
  </div>

  <!-- Full person editor (includes Quick Sign-In Code). Host z-index must sit
       above LeaseSearch popups (.edit-dialog is z-index 1001). -->
  <div v-if="showPersonEditor" class="person-editor-host">
    <NewPerson
      :initialTenantQuery="personEditorQuery"
      :personToEdit="personToEdit"
      :startInAddMode="personEditorStartAdd"
      :leaseId="selectedLease ? selectedLease.id : null"
      :leaseCurrent="isSelectedLeaseCurrent"
      :primaryBlocked="editorPrimaryBlocked"
      @closeDialog="onPersonEditorClosed"
    />
  </div>

  <!-- Document upload widget -->
  <div v-if="showUploadDialog">
    <DocumentUpload />
  </div>

  <!-- Hidden audio element for camera shutter sound -->
  <audio ref="shutterSound" src="/camera-shutter-6305.mp3"></audio>
</template>

<script>
import NotesPanel from './NotesPanel.vue'; // Existing import
import DocumentUpload from './DocumentUpload.vue'; // Existing import
import NewPerson from './NewPerson.vue';
import axios from '@/axiosConfig'; // Adjust based on your axios configuration
import { uploadChunked } from '@/chunkedUpload';
import moment from 'moment'; // For date manipulation
import {
  expectedRentAmount,
  expectedRentLabel,
  isExpectedRentDescription,
} from '@/utils/expectedRent';
import {
  activeAddressFromPerson,
  leaseSendAddresses,
} from '@/utils/personEmailRows';
import html2canvas from 'html2canvas'; // For capturing HTML elements as canvas
import { marked } from 'marked';
import eventBus from './eventBus.js';

function leaseLocalDay(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return new Date(
      value.getFullYear(),
      value.getMonth(),
      value.getDate(),
    ).getTime();
  }
  const text = String(value).trim();
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(text);
  if (match) {
    const day = new Date(
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3]),
    );
    return Number.isNaN(day.getTime()) ? null : day.getTime();
  }
  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(
    parsed.getFullYear(),
    parsed.getMonth(),
    parsed.getDate(),
  ).getTime();
}

export default {
  components: { NotesPanel, DocumentUpload, NewPerson },

  data() {
    return {
      showQuickBooksTool: false,
      /* for tempData di */
      namePattern: '216 John 3R',
      afterDate: '2023-06-01',
      tempData: [],
      filteredTempData: [],
      selectedTempRow: null,

      isAdding: false,
      users: [],
      myUserId: null,
      myUserRole: null,
      viewerTitle: 'Check Viewer',
      firstTimeSearched: true,
      showLedgerForm: false,
      showSubForm: false,
      formMode: null,
      formTitle: 'Add, Edit or Delete ledger entries',
      isLeaseLedgerRecord: false,
      isLeasePaymentRecord: false,
      ledgerErrorMessage: '',
      predefinedOptions: [
        'Bounced Check',
        'Convenience Fee',
        'Court Fee',
        'Transfer to Subsequent Lease',
        'overpayment refund',
        'ending balance writeoff',
        'Early resign discount',
        'Late fee',
        'Manual Pmt entry',
        'Opening balance from prior lease',
        'Offset for rejected DEX payment',
        'Parking fee',
        'Early Payment offer 5',
        'Other...',
      ], // Predefined descriptions
      selectedPredefined: '', // Value from dropdown
      accountOptions: [], // Account options for lease ledger dropdown
      form: {
        date: new Date().toISOString().slice(0, 10), // Default to today's date
        description: '',
        credit: null,
        debit: null,
        num: '',
        type: 'x', // Default to 'Choose...'
        account: null, // Account from chart_of_accounts
      },
      addNewLedgerEntry: false,
      showLedgerPanel: true,
      tenantSearchResults: [],
      tenantSearchQuery: '',
      searchQuery: '',
      properties: [],
      debounceTimeout: null,
      selectedProperty: null,
      selectedLease: null,
      selectedTenant: null,
      tenants: [],
      leasePayments: [],
      tenantPayments: [],
      tph_headers: ['Date', 'Amount', 'Name', 'Transact.#', 'T', 'P', 'N'],
      matchingDocuments: [],
      showEye: null,
      selectedDocument: null,
      pdfData: null,
      pdfUrl: null,
      showUploadDialog: false, // Controls the visibility of the dialog
      uploadFile: null, // Holds the uploaded file
      uploadError: '', // Holds any error message
      documentTitle: '', // The title for the new document
      showEditLeaseDialog: false,
      editedLease: {
        id: null,
        name: '',
        signedOnRaw: null, // New field for Signed On date
        checklistRaw: null,
        startsOnRaw: null,
        endsOnRaw: null,
        monthlyBaseRent: '',
        prorated: '',
        remaining: '',
        securityDeposit: '',
        secDepPaidOnRaw: null,
        totalLeasePayment: '',
        petFee: '',
        petCount: 1,
        includesGas: false,
        includesElectric: false,
        includesWaterSewer: false,
        includesTrash: false,
        includesPhone: false,
        includesCableTV: false,
        includesWalkSnowRemoval: false,
        includesParkingSnowRemoval: false,
        includesLawncare: false,
        buildingCode: '',
        maintenanceCode: '',
        apartmentCode: '',
        garageCode: '',
        // New additional services fields
        hasParking: false,
        parkingSpot: '',
        parkingFee: '',
        parkingStartRaw: null,
        hasStorage: false,
        storageDesc: '',
        storageFee: '',
        storageStartRaw: null,
        hasMobileAC: false,
        mobileACModel: '',
        mobileACRent: '',
        mobileACStartRaw: null,
        dogCount: 0,
        catCount: 0,
        petNames: '',
        dogTemperament: 0,
        catTemperament: 0,
      },
      showNotesPanel: false,
      showNoteDialog: false,
      noteSearchQuery: '',
      notes: [],
      noteType: '',
      noteId: null,
      editingNote: null,
      newNoteTitle: '',
      newNoteBody: '',
      showAllDocs: '(show)',
      isOverflowing: false,
      dragOffset: { x: 0, y: 0 },
      showDialogPopup: false,
      selectedRow: null,
      showPaymentNoteDialog: false,
      paymentNote: '',
      selectedPaymentId: null,
      selectedPaymentIds: [],
      showTenantSearchDialog: false,
      showPersonEditor: false,
      personEditorQuery: '',
      personEditorStartAdd: false,
      personToEdit: null,
      selectedPerson: null,
      clickTimeout: null,
      leaseClickTimeout: null,

      ledgerSummary: {
        // sample data that will be replaced
        property: 'property',
        tenant: 'Tenant',
        securityDeposit: 1000.0,
        startsOn: '2024-07-12',
        endsOn: '2025-06-30',
        monthlyBaseRent: 100.0,
        prorated: 400.0,
        paidOn: '2024-07-10',
        paymentRef: '5923',
      },

      ledgerHeaders: [
        'Date',
        'Description',
        'Credits',
        'Debits',
        'Tr# (payer initials)',
        'S',
        'Type',
        'Running Balance',
        'Expected Balance',
        'Net Balance',
        'Note',
        'Doc',
      ],

      ledgerData: [],
      selectedLedgerRow: null,
      selectedLedgerRows: new Set(),
      isMouseOverLedger: false,
      showEditPopup: false, // Controls the visibility of the popup
      currentRecordType: null, // To identify if it's a lease_payment or lease_ledger
      editedRecord: null, // Stores the selected record for editing
      showEffectiveDate: false,

      showEmailComposer: false, // Controls visibility of the email composition
      subject: '', // Bound to the subject input
      emailBodyTop:
        'Dear Tenants,<br><br>\nPlease see your attached ledger. You are currently carrying a balance as shown. Advise us how you plan to bring your account balance back to zero.<br><br>', // Bound to the body above the image
      emailBodyBottom:
        '<br><br>Regards,<br>**The Pixie Properties Team**<br>616-916-6953', // Bound to the body below the image
      capturedImage: '', // Holds the base64 image from html2c
      recipients: [],
      showHelp: false,
      ledgerHelp: false,
      validationMessage: '',
      showPDF: false,
      pdfURL: '',
      composeEmailPreview: '',
      showLedgerListPopup: false,
      ledgerList: [], // Stores the list of ledger entries
      isDragMode: false,
      popupPosition: { x: 100, y: 100 }, // Initial popup position
      currentPopup: null, // Holds the currently targeted popup reference
      currentIconOffset: { x: 0, y: 0 }, // Holds the icon offset for dynamic calculations
      isPopupDragging: false,
      activeDraggedPopup: null,
      popupDragOffset: { x: 0, y: 0 },
      recalcLedger: false,
      replacingItem: false,
      demandBalanceOverride: 0,
      isLedgerPrinting: false, // New state to track printing in progress
    };
  },

  created() {
    this.fetchUsers();
    document.title = 'Lease Search';
    this.debouncedSearch = this.debounce(this.onSearch, 300);
    this.debouncedTenantSearch = this.debounce(this.onSearchTenantQuery, 300);
    // Automatically set searchQuery from URL query parameter and perform search
    if (this.$route.query.search && this.firstTimeSearched) {
      this.searchQuery = this.$route.query.search;
      this.firstTimeSearched = false;
      this.onSearch(); // Trigger search when component is created
    }
    window.addEventListener('keydown', this.handleKeyDown);

    eventBus.on('closeDocumentUpload', () => {
      this.showUploadDialog = false;
    });
  },

  mounted() {
    this.$nextTick(() => {
      this.checkOverflow(); // Ensure the DOM is fully loaded
    });

    window.addEventListener('resize', this.checkOverflow);
    document.addEventListener('mousemove', this.handlePopupDrag);
    document.addEventListener('mouseup', this.stopPopupDrag);
    this.showLedgerPanel = true;
    this.fetchAccountOptions();
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.checkOverflow);
    window.removeEventListener('keydown', this.handleKeyDown);
    document.removeEventListener('mousemove', this.handlePopupDrag);
    document.removeEventListener('mouseup', this.stopPopupDrag);
    eventBus.off('closeDocumentUpload');
  },

  computed: {
    isSelectedLeaseCurrent() {
      const lease = this.selectedLease;
      if (!lease || !lease.startsOn || !lease.endsOn) return false;
      const start = leaseLocalDay(lease.startsOn);
      const end = leaseLocalDay(lease.endsOn);
      if (start == null || end == null) return false;
      const now = new Date();
      const today = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
      ).getTime();
      return today >= start && today <= end;
    },
    editorPrimaryBlocked() {
      if (!this.personToEdit) return false;
      const editId = this.personToEdit.id;
      return (this.tenants || []).some(
        (tenant) =>
          tenant &&
          tenant.id != editId &&
          Number(tenant.isPrimaryTenant) === 1,
      );
    },
    balanceMessage() {
      let result;
      if (this.selectedLease) {
        let balance = this.selectedLease.balance;

        // Force balance to zero if lease hasn't started yet
        const today = new Date();
        const leaseStartDate = new Date(this.selectedLease.startsOn);
        if (leaseStartDate > today) {
          balance = 0;
        }

        const formattedBalance = Math.abs(balance).toLocaleString(undefined, {
          style: 'currency',
          currency: 'USD',
        });

        switch (true) {
          case balance === 0:
            result = 'Balance: $0.00';
            break;
          case balance < 0:
            result = `You owe: ${formattedBalance}`;
            break;
          default:
            result = `You have a credit of: ${formattedBalance}`;
            break;
        }
      }
      return result;
    },

    compiledMarkdown() {
      // Set up marked with GFM enabled
      marked.setOptions({
        gfm: true,
        breaks: true, // Optional: Enable line breaks like in GitHub comments
      });

      // Return the compiled HTML from markdown content
      return marked(this.markdownContent);
    },

    filteredProperties() {
      if (!this.properties) return [];

      // Split searchQuery into tokens by comma and trim whitespace, ignoring the first token
      const tokens = this.searchQuery
        .split(',')
        // .slice(1)
        .map((token) => token.trim().toLowerCase()); // Remove '#' from tokens

      // If there are no tokens to filter by, return all properties
      if (tokens.length === 0) {
        return this.properties;
      }

      // Filter properties that match all tokens
      return this.properties.filter((property) => {
        return tokens.every(
          (token) => property.display.toLowerCase().includes(token), // Remove '#' from property display string
        );
      });
    },

    filteredLedgerData() {
      return this.ledgerData.map((row) => {
        const allValues = Object.values(row);
        // Keep first 12 values (indices 0-11) which includes all display columns including Doc
        const values = allValues.slice(0, 12);
        return { values, originalRow: row };
      });
    },

    showFormCondition() {
      return (
        this.showLedgerForm &&
        (this.addNewLedgerEntry ||
          (this.ledgerData[this.selectedLedgerRow] &&
            (this.ledgerData[this.selectedLedgerRow].ledgerId ||
              this.ledgerData[this.selectedLedgerRow].paymentId)))
      );
    },
    totalCredit() {
      return this.filteredLedgerData
        .reduce((sum, row) => {
          const creditValue = parseFloat(row.values[2]) || 0; // Column 2 for Credit
          return sum + creditValue;
        }, 0)
        .toFixed(2);
    },
    totalDebit() {
      return this.filteredLedgerData
        .reduce((sum, row) => {
          const debitValue = parseFloat(row.values[3]) || 0; // Column 3 for Debit
          return sum + debitValue;
        }, 0)
        .toFixed(2);
    },
    formattedTotalCredit() {
      return this.formatCurrency(this.totalCredit);
    },
    formattedTotalDebit() {
      return this.formatCurrency(this.totalDebit);
    },
    selectedTotal() {
      if (this.selectedLedgerRows.size === 0) return 0;
      let total = 0;
      for (const idx of this.selectedLedgerRows) {
        const row = this.filteredLedgerData[idx];
        if (row) {
          total += parseFloat(row.values[2]) || 0; // credit
          total -= parseFloat(row.values[3]) || 0; // debit
        }
      }
      return total;
    },
    formattedSelectedTotal() {
      return (
        this.formatCurrency(Math.abs(this.selectedTotal).toFixed(2)) +
        (this.selectedTotal < 0 ? ' (debit)' : ' (credit)')
      );
    },

    getPurposeIcon() {
      // 0=undefined, 1=rent, 2=prorated, 3=fees, 4=security deposit, 5=application
      return (purpose) => {
        switch (purpose) {
          case 1:
            return {
              html: 'R <i class="fas fa-house"></i>',
              title: 'rent',
            };
          case 2:
            return {
              html: 'P <i class="fas fa-house-medical"></i>',
              title: 'Prorated',
            };
          case 3:
            return {
              html: 'F <i class="fas fa-money-check-dollar"></i>',
              title: 'fees',
            };
          case 4:
            return {
              html: 'S <i class="fas fa-piggy-bank"></i>',
              title: 'sec deposit',
            };
          case 5:
            return {
              html: 'A <i class="fas fa-coins"></i>',
              title: 'Application',
            };
          default:
            return {
              html: '<i class="fas fa-question"></i>',
              title: 'unknown',
            };
        }
      };
    },

    getPaymentIcon() {
      return (method) => {
        switch (method) {
          case 1:
            return {
              html: 'P <i class="fas fa-money-check"></i>',
              title: 'paper check',
            };
          case 2:
            return {
              html: 'E <i class="fas fa-money-check-alt"></i>',
              title: 'E-check (DEX)',
            };
          case 3:
            return {
              html: 'C <i class="fas fa-money-check"></i>',
              title: 'cashiers check',
            };
          case 4:
            return {
              html: 'O <i class="fas fa-money-bill"></i>',
              title: 'other',
            };
          case 5:
            return {
              html: 'M <i class="fas fa-money-check-alt"></i>',
              title: 'money order',
            };
          case 6:
            return {
              html: 'B <i class="fas fa-file-invoice-dollar"></i>',
              title: 'billpayment svc',
            };
          default:
            return {
              html: '<i class="fas fa-question"></i>',
              title: 'unknown',
            };
        }
      };
    },
  },

  methods: {
    activeAddressFromPerson,
    async fetchAccountOptions() {
      try {
        const response = await axios.get(
          '/api/chart-of-accounts/ledger-accounts',
        );
        this.accountOptions = response.data.map((acct) => ({
          id: acct.id,
          value: `${acct.accountNumber} : ${acct.accountName}`,
          label: `${acct.accountNumber} : ${acct.accountName}`,
        }));
      } catch (error) {
        console.error('Error fetching account options:', error);
      }
    },

    isDaysOld(dateString, days) {
      if (!dateString) return false;
      const paymentDate = new Date(dateString);
      const today = new Date();
      const diffTime = Math.abs(today - paymentDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays > days;
    },

    insertQuickAction(action) {
      switch (action) {
        case 'rentReminder':
          this.subject = 'Pixie Properties: Rent Reminder';
          this.emailBodyTop =
            'Dear Tenants,<br><br>\nThis is a friendly reminder that your rent is due on the first, and a $40 late fee applies for payments received after the 5th. Please ensure timely payment to avoid any future late fees.<br><br>';
          break;
        case 'balanceDue':
          this.subject = 'Pixie Properties: Balance Due';
          this.emailBodyTop =
            'Dear Tenants,<br><br>\nPlease be advised that you have a balance due on your account. We kindly request that you address this matter at your earliest convenience.<br><br>';
          break;
        case 'information':
          this.subject = 'Pixie Properties: Your current ledger';
          this.emailBodyTop =
            'Dear Tenants,<br><br>\nHere is your current Pixie Properties Ledger. No action is needed at this time, this is just for information.<br><br>';
          break;
      }
      this.updatePreview();
    },

    evaluateDemandBalanceExpression() {
      const value = this.demandBalanceOverride;

      // If empty or null, do nothing
      if (!value || value === '' || value === 0) {
        return;
      }

      // Convert to string for evaluation
      const expression = String(value).trim();

      // Check if it contains mathematical operators
      if (/[+\-*/()]/.test(expression)) {
        try {
          // Create a safe evaluation by only allowing numbers and basic operators
          // Remove any characters that aren't numbers, operators, parentheses, or decimal points
          const sanitized = expression.replace(/[^0-9+\-*/.()\s]/g, '');

          if (sanitized) {
            // Use Function constructor for safer evaluation than eval()
            const result = new Function('return ' + sanitized)();

            // Update the input with the calculated result
            if (!isNaN(result) && isFinite(result)) {
              this.demandBalanceOverride = Math.round(result * 100) / 100; // Round to 2 decimal places
            }
          }
        } catch (error) {
          console.error('Error evaluating expression:', error);
          // Keep the original value if evaluation fails
        }
      }
    }, // Function to share the current window URL

    shareURL(id) {
      try {
        let url = new URL(window.location.href);

        // Remove the existing 'search' parameter if it exists
        url.searchParams.delete('search');

        // Append the new 'search' parameter
        url = url + '?search=:' + id;

        if (navigator.clipboard && window.isSecureContext) {
          navigator.clipboard
            .writeText(url.toString())
            .then(() => console.log('URL copied to clipboard.'))
            .catch((err) => console.error('Failed to copy URL:', err));
        } else {
          console.log(
            'Clipboard feature is not available; would have copied ',
            url.toString(),
          );
        }
      } catch (error) {
        console.error('Error processing the URL:', error);
      }
    },

    openUploadDialog() {
      this.documentTitle = `lease for ${this.selectedProperty.display}`; // Preload title
      this.showUploadDialog = !this.showUploadDialog;
      this.$nextTick(() => {
        const dialogElement = document.querySelector('.upload-dialog');
        dialogElement.style.left = `${event.clientX + 30}px`;
        dialogElement.style.top = `${event.clientY + 30}px`;
      });
    },

    async fetchUsers() {
      try {
        const response = await axios.get('/api/users');
        this.users = response.data || [];
        const currentUser = this.users
          ? this.users.find((user) => user.you === 1)
          : null; // Find the user where 'you' === 1
        // console.log('userdata:', this.users);
        // console.log('Curruser:', currentUser);
        this.myUserId = currentUser.id;
        this.myUserRole = currentUser.role;
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    },

    updatePreview() {
      this.composeEmailPreview = marked.parse(
        this.emailBodyTop + this.emailBodyBottom,
      );
    },

    debounce(func, delay) {
      let timeout;
      return function (...args) {
        clearTimeout(timeout); // Clear the previous timeout
        timeout = setTimeout(() => {
          func.apply(this, args); // Apply the function after the delay
        }, delay);
      }.bind(this);
    },

    toggleShowAllDocs() {
      if (this.showAllDocs == '(hide)') this.showAllDocs = '(show)';
      else this.showAllDocs = '(hide)';
      this.fetchDocuments(this.selectedProperty);
    },

    onSearch() {
      // console.log('onSearch 1978');
      // Extract the first token from the search query
      const firstToken = this.searchQuery.split(',')[0].trim();

      axios
        .get('/api/properties', { params: { query: firstToken } })
        .then((response) => {
          this.properties = response.data;
          // Automatically set selectedProperty to the first result if a search was triggered by a URL query parameter
          if (this.properties.length == 1) {
            this.selectedProperty = this.properties[0];
            if (this.searchQuery.startsWith(':'))
              this.selectProperty(this.selectedProperty);
          } else {
            this.selectedProperty = null; // Clear selection if no results
          }
        })
        .catch((error) => {
          console.error('Error fetching properties:', error);
        });
    },

    clearNoteSearch() {
      this.noteSearchQuery = '';
    },

    clearSearch() {
      this.searchQuery = '';
      this.properties = [];
      this.selectedProperty = null;
      this.selectedDocument = null;
      this.selectedLease = null;
      this.selectedTenant = null;
      this.tenants = [];
      this.leasePayments = [];
      this.tenantPayments = [];
    },

    highlightTerm(queryInput, text) {
      if (queryInput) {
        // Split the query input by commas and trim whitespace from each token
        const tokens = queryInput.split(',').map((token) => token.trim());

        // Determine the query term: use the first token if there's only one, otherwise use the last token
        const query =
          tokens.length === 1 ? tokens[0] : tokens[tokens.length - 1];

        // Escape special characters for regex
        const escapedQuery = query.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
        const regex = new RegExp(`(${escapedQuery})`, 'gi');

        if (text) {
          // Highlight the term in the text
          return text.replace(regex, '<span class="highlight">$1</span>');
        }
      }

      // Return the original text if there's no query input or no text provided
      return text;
    },

    async selectProperty(property) {
      // console.log('selectProperty 2039');
      // this.searchQuery = property.display;
      this.selectedProperty = property;
      this.selectedLease = null;
      this.selectedTenant = null;
      this.selectedDocument = null;
      // for the quickbooks lookup tool
      if (property.display) {
        this.namePattern = property.display
          .replace('Apt ', '#')
          .replace('# ', '#')
          .replace('House', '');
        this.tempData = [];
        this.fetchTempData();
      }

      this.tenants = [];
      this.leasePayments = [];
      this.tenantPayments = [];
      await this.fetchLeases(property.id);
      await this.fetchDocuments(this.selectedProperty);
    },

    async selectDocument(doc, event) {
      // console.log('selectDocument 2063');
      let suppressPanel = false;
      if (doc && doc.Id && Object.keys(doc).length === 1) {
        suppressPanel = true;
        const foundDocument = this.matchingDocuments.find(
          (d) => d.Id === doc.Id,
        );
        if (foundDocument) {
          doc = foundDocument;
        } else {
          if (!this.searchQuery.startsWith(':'))
            console.error(
              `Document with Id ${doc.Id} not found in matchingDocuments`,
            );
          return;
        }
      }

      if (event && event.shiftKey) {
        this.$router.push({
          path: '/documents',
          query: { search: `:${doc.Id}` },
        });
      } else {
        this.selectedDocument = doc;
        if (!suppressPanel) this.showLedgerPanel = false;

        try {
          // Fetch the PDF directly from the backend
          const response = await axios.get(`/api/load-pdf/${doc.Id}`, {
            responseType: 'arraybuffer',
          });

          if (response.status === 200) {
            // Create a Blob from the response data
            const pdfBlob = new Blob([response.data], {
              type: 'application/pdf',
            });
            const pdfUrl = URL.createObjectURL(pdfBlob);

            // Set the URL for your iframe or download link
            this.pdfUrl = pdfUrl;
          } else {
            console.error(
              `Failed to load PDF for document ID ${doc.Id}. Status: ${response.status}`,
            );
          }
        } catch (error) {
          console.error('Error loading PDF:', error);
        }
      }
    },
    async selectLease(lease) {
      if (!lease) {
        console.warn('selectLease called with null or undefined lease');
        return;
      }

      this.selectedLease = lease;
      this.selectedTenant = null;
      this.tenants = [];
      // Create a Date object by parsing the MySQL datetime
      const endDate = new Date(lease.startsOn.replace(' ', 'T')); // Replacing space for ISO format
      // Subtract 3 days
      endDate.setDate(endDate.getDate() - 3);
      // Format back to 'YYYY-MM-DD'
      this.afterDate = endDate.toISOString().slice(0, 10);
      // this.fetchTempData();
      this.tenantPayments = [];
      if (this.selectedProperty) {
        await this.fetchDocuments(this.selectedProperty);
      }
      await this.fetchLeasePayments(lease.id);
      await this.fetchTenants(lease.id);
      if (this.tenants.length > 0) this.selectTenant(this.tenants[0]);
      this.selectedDocument = null;
      if (lease.documentId) this.selectDocument({ Id: lease.documentId });
      // this.showLedgerPanel = true;
      document.title =
        'Lease Search: ' + (this.selectedProperty?.display || 'No Property');
    },

    async selectTenant(tenant) {
      this.selectedTenant = tenant;
      this.selectedPaymentId = null;
      this.fetchTenantPayments(tenant.id);
      this.selectedPaymentIds = [];
    },

    async fetchLeases(propertyId) {
      // Fetch leases for the selected property
      return axios
        .get(`/api/leases/${propertyId}`)
        .then((response) => {
          this.selectedProperty.leases = response.data;
          if (
            this.$route.query.search &&
            this.selectedProperty.leases.length > 0
          ) {
            if (this.searchQuery.startsWith(':')) {
              // Extract the number following the colon as leaseId
              const leaseId = parseInt(this.searchQuery.slice(1), 10);

              // Find the lease within this.selectedProperty.leases with the matching id
              const leaseToSelect = this.selectedProperty.leases.find(
                (lease) => lease.id === leaseId,
              );

              // If a matching lease is found, select it using this.selectLease
              if (leaseToSelect) {
                this.selectLease(leaseToSelect);
              }
            } else {
              const now = new Date();
              const leaseToSelect = this.selectedProperty.leases.find(
                (lease) =>
                  new Date(lease.startsOn) <= now &&
                  new Date(lease.endsOn) >= now,
              );

              if (leaseToSelect) {
                this.selectLease(leaseToSelect);
              } else {
                this.selectLease(this.selectedProperty.leases[0]);
              }
            }
          }
        })
        .catch((error) => {
          console.error('Error fetching leases:', error);
        });
    },

    // TODO: review if this is redundant
    async fetchLeasePayments(leaseId) {
      return axios
        .get(`/api/lease-payments/${leaseId}`)
        .then((response) => {
          this.leasePayments = response.data;
        })
        .catch((error) => {
          console.error('Error fetching lease payments:', error);
        });
    },

    async fetchTenants(leaseId) {
      return axios
        .get(`/api/people/${leaseId}`)
        .then((response) => {
          this.tenants = response.data;
        })
        .catch((error) => {
          console.error('Error fetching tenants:', error);
        });
    },
    listClickSearch(item) {
      this.searchQuery = ':' + item.leaseId;
      this.showLedgerPanel = false;
      this.onSearch();
    },

    async fetchTenantPayments(peopleId) {
      return axios
        .get(`/api/tenant-payments/${peopleId}`)
        .then((response) => {
          this.tenantPayments = response.data;

          // Automatically select payments that match selectedLease.id or paymentId is in selectedPaymentIds
          this.selectedPaymentIds = this.tenantPayments
            .filter(
              (payment) =>
                payment.leaseId === this.selectedLease.id ||
                this.selectedPaymentIds.includes(payment.id),
            )
            .map((payment) => payment.id);
        })
        .catch((error) => {
          console.error('Error fetching tenant payments:', error);
        });
    },

    async fetchDocuments(selectedProperty) {
      const showDeleted = this.showAllDocs == '(hide)' ? 1 : 0;
      const modifiedProperty = { ...selectedProperty };
      delete modifiedProperty.leases;

      return axios
        .get('/api/documents', {
          params: { ...modifiedProperty, showDeleted },
        })
        .then((response) => {
          this.matchingDocuments = response.data;
        })
        .catch((error) => {
          console.error('Error fetching documents payments:', error);
        });
    },

    formatDate(dateInput) {
      // If the input is a string, directly parse it; if it's a Date object, handle it as is
      if (!dateInput) return '[--/--/--]';

      const date =
        typeof dateInput === 'string' ? new Date(dateInput) : dateInput;

      if (isNaN(date.getTime())) {
        // Return a default value if the date is invalid
        return 'Invalid Date';
      }

      // Extract the local date components without any timezone adjustments
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0'); // getMonth is 0-indexed, so add 1
      const day = String(date.getDate()).padStart(2, '0');

      // Return the formatted date as MM/DD/YYYY
      const formatted = `${month}/${day}/${year}`;
      // console.log(dateInput, formatted);
      return formatted;
    },

    formatDateTime(dateString) {
      const options = {
        year: '2-digit',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'EST',
      };
      return new Date(dateString).toLocaleDateString(undefined, options);
    },

    formatRent(rent) {
      const rentNumber = parseFloat(rent);
      if (isNaN(rentNumber)) {
        return rent; // Return the original value if it's not a valid number
      }
      return rentNumber.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    formatBalanceOverride(balance) {
      const balanceNumber = parseFloat(balance);
      if (isNaN(balanceNumber) || balanceNumber === 0) {
        return '0.00'; // Show 0.00 if blank or zero
      }
      return balanceNumber.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    isToday(date) {
      const today = new Date().toISOString().split('T')[0];
      return today === date.split('T')[0];
    },

    //for lease editing
    openEditLeaseDialog(lease) {
      this.showLedgerPanel = false;
      clearTimeout(this.leaseClickTimeout);
      this.validationMessage = '';
      if (lease) {
        this.editedLease = {
          ...lease,
          secDepPaidOnRaw: moment(lease.secDepPaidOn).format('YYYY-MM-DD'),
          signedOnRaw: moment(lease.signedOn).format('YYYY-MM-DD'),
          checklistRaw: moment(lease.checklistDate).format('YYYY-MM-DD'),
          startsOnRaw: moment(lease.startsOn).format('YYYY-MM-DD'),
          endsOnRaw: moment(lease.endsOn).format('YYYY-MM-DD'),
          prorated:
            lease.prorated !== undefined && lease.prorated !== null
              ? parseFloat(lease.prorated).toFixed(2)
              : '0.00',
          remaining:
            lease.remaining !== undefined && lease.remaining !== null
              ? parseFloat(lease.remaining).toFixed(2)
              : '0.00',
          totalLeasePayment:
            lease.totalLeasePayment !== null
              ? parseFloat(lease.totalLeasePayment).toFixed(2)
              : '0.00',
          monthlyBaseRent:
            lease.monthlyBaseRent !== undefined &&
            lease.monthlyBaseRent !== null
              ? parseFloat(lease.monthlyBaseRent).toFixed(2)
              : '0.00',
          includesWaterSewer: !!lease.includesWaterSewer,
          includesGas: !!lease.includesGas,
          includesElectric: !!lease.includesElectric,
          includesTrash: !!lease.includesTrash,
          includesPhone: !!lease.includesPhone,
          includesCableTV: !!lease.includesCableTV,
          includesWalkSnowRemoval: !!lease.includesWalkSnowRemoval,
          includesParkingSnowRemoval: !!lease.includesParkingSnowRemoval,
          includesLawncare: !!lease.includesLawncare,
          earlyResign: !!lease.earlyResign,
          // New additional services fields
          hasParking: !!lease.hasParking,
          parkingSpot: lease.parkingSpot || '',
          parkingFee:
            lease.parkingFee !== undefined && lease.parkingFee !== null
              ? parseFloat(lease.parkingFee).toFixed(2)
              : '0.00',
          parkingStartRaw: lease.parkingStart
            ? moment(lease.parkingStart).format('YYYY-MM-DD')
            : null,
          hasStorage: !!lease.hasStorage,
          storageDesc: lease.storageDesc || '',
          storageFee:
            lease.storageFee !== undefined && lease.storageFee !== null
              ? parseFloat(lease.storageFee).toFixed(2)
              : '0.00',
          storageStartRaw: lease.storageStart
            ? moment(lease.storageStart).format('YYYY-MM-DD')
            : null,
          hasMobileAC: !!lease.hasMobileAC,
          mobileACModel: lease.mobileACModel || '',
          mobileACRent:
            lease.mobileACRent !== undefined && lease.mobileACRent !== null
              ? parseFloat(lease.mobileACRent).toFixed(2)
              : '0.00',
          mobileACStartRaw: lease.mobileACStart
            ? moment(lease.mobileACStart).format('YYYY-MM-DD')
            : null,
          dogCount: lease.dogCount || 0,
          catCount: lease.catCount || 0,
          petNames: lease.petNames || '',
          dogTemperament: lease.dogTemperament || 0,
          catTemperament: lease.catTemperament || 0,
        };
        // console.log(this.editedLease)
      } else {
        // Clear the editedLease object to prepare for a new lease creation
        this.addnewLease();
      }

      this.showEditLeaseDialog = true;
    },

    closeEditLeaseDialog() {
      this.showEditLeaseDialog = false;
    },

    fillServiceStartIfBlank(feeField, startField) {
      const fee =
        parseFloat(
          (this.editedLease[feeField] || '').toString().replace(/,/g, ''),
        ) || 0;
      if (
        fee !== 0 &&
        !this.editedLease[startField] &&
        this.editedLease.startsOnRaw
      ) {
        this.editedLease[startField] = this.editedLease.startsOnRaw;
      }
    },

    onParkingFeeBlur() {
      this.fillServiceStartIfBlank('parkingFee', 'parkingStartRaw');
    },

    onStorageFeeBlur() {
      this.fillServiceStartIfBlank('storageFee', 'storageStartRaw');
    },

    onMobileACRentBlur() {
      this.fillServiceStartIfBlank('mobileACRent', 'mobileACStartRaw');
    },

    startOfDay(date) {
      const d = new Date(date);
      d.setHours(0, 0, 0, 0);
      return d;
    },

    // Parse YYYY-MM-DD (or datetime prefix) as local midnight — avoids UTC shift.
    parseLocalDate(dateInput) {
      if (!dateInput) {
        return null;
      }
      const str = String(dateInput);
      const match = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (match) {
        return new Date(
          Number(match[1]),
          Number(match[2]) - 1,
          Number(match[3]),
        );
      }
      const d = new Date(dateInput);
      return isNaN(d.getTime()) ? null : this.startOfDay(d);
    },

    getMonthlyExpectedRentForRow(rowDate, lease) {
      return expectedRentAmount(rowDate, lease);
    },

    getExpectedRentPmtLabel(rowDate, lease) {
      return expectedRentLabel(rowDate, lease);
    },

    getExpectedDeductionsThrough(lease, throughDate) {
      const startsOnDate = new Date(lease.startsOn);
      const endMonth = new Date(lease.endsOn);
      const throughMonthStart = new Date(
        throughDate.getFullYear(),
        throughDate.getMonth(),
        1,
      );
      const prorated = parseFloat(lease.prorated) || 0;
      const hasProrated = prorated !== 0 && lease.prorated;

      let currentMonth = hasProrated
        ? new Date(
            startsOnDate.getFullYear(),
            startsOnDate.getMonth(),
            startsOnDate.getDate(),
          )
        : new Date(startsOnDate.getFullYear(), startsOnDate.getMonth(), 1);

      let monthIndex = 0;
      let totalDue = 0;

      while (
        currentMonth.getFullYear() < endMonth.getFullYear() ||
        (currentMonth.getFullYear() === endMonth.getFullYear() &&
          currentMonth.getMonth() <= endMonth.getMonth())
      ) {
        const monthStartDate = new Date(currentMonth);
        const monthStart = new Date(
          monthStartDate.getFullYear(),
          monthStartDate.getMonth(),
          1,
        );

        if (monthStart > throughMonthStart) {
          break;
        }

        if (hasProrated) {
          if (monthIndex === 0) {
            totalDue += prorated;
          } else {
            totalDue += this.getMonthlyExpectedRentForRow(
              monthStartDate,
              lease,
            );
          }
        } else if (
          (lease.prorated === null || parseFloat(lease.prorated) === 0) &&
          startsOnDate.getDate() !== 1
        ) {
          if (monthIndex > 0) {
            totalDue += this.getMonthlyExpectedRentForRow(
              monthStartDate,
              lease,
            );
          }
        } else if (
          (lease.prorated === null || parseFloat(lease.prorated) === 0) &&
          startsOnDate.getDate() === 1
        ) {
          totalDue += this.getMonthlyExpectedRentForRow(monthStartDate, lease);
        }

        currentMonth.setMonth(currentMonth.getMonth() + 1);
        currentMonth.setDate(1);
        monthIndex++;
      }

      return totalDue;
    },

    async saveLeaseEdits() {
      this.validationMessage = '';
      const validationMessages = [];

      const secDepTime = new Date(this.editedLease.secDepPaidOnRaw).getTime();
      if (isNaN(secDepTime) || secDepTime === 0) {
        validationMessages.push('Sec Dep date invalid.');
      }

      const checklistTime = new Date(this.editedLease.checklistRaw).getTime();
      if (isNaN(checklistTime) || checklistTime === 0) {
        validationMessages.push('Checklist date invalid.');
      }

      const signedOnTime = new Date(this.editedLease.signedOnRaw).getTime();
      if (isNaN(signedOnTime) || signedOnTime === 0) {
        validationMessages.push('Signed on date invalid.');
      }

      const startsOnTime = new Date(this.editedLease.startsOnRaw).getTime();
      if (isNaN(startsOnTime) || startsOnTime === 0) {
        validationMessages.push('Start date invalid.');
      }

      const endsOnTime = new Date(this.editedLease.endsOnRaw).getTime();
      if (isNaN(endsOnTime) || endsOnTime === 0) {
        validationMessages.push('Ends on date invalid.');
      }

      // Combine messages if any are present
      this.validationMessage = validationMessages.join(' ');

      if (this.editedLease.monthlyBaseRent == 0) {
        this.validationMessage = 'rent must be valid';
      }
      if (this.editedLease.totalLeasePayment == 0) {
        this.validationMessage = 'total Lease Payment must be valid';
      }
      if (this.editedLease.securityDeposit == 0) {
        this.validationMessage = 'security deposit must be valid';
      }

      if (this.validationMessage != '') {
        return;
      }

      try {
        const formattedLease = {
          ...this.editedLease,
          propertyId: this.selectedProperty.id,
          name: this.editedLease.name,
          secDepPaidOn: moment(this.editedLease.secDepPaidOnRaw).format(
            'YYYY-MM-DD',
          ),
          signedOn: moment(this.editedLease.signedOnRaw).format('YYYY-MM-DD'),
          startsOn: moment(this.editedLease.startsOnRaw).format('YYYY-MM-DD'),
          endsOn: moment(this.editedLease.endsOnRaw).format('YYYY-MM-DD'),
          checklistDate: moment(this.editedLease.checklistRaw).format(
            'YYYY-MM-DD',
          ),
          monthlyBaseRent: parseFloat(
            this.editedLease.monthlyBaseRent
              ? this.editedLease.monthlyBaseRent.replace(/,/g, '')
              : '0',
          ),
          prorated: parseFloat(
            this.editedLease.prorated
              ? this.editedLease.prorated.replace(/,/g, '')
              : '0',
          ),
          securityDeposit: parseFloat(
            this.editedLease.securityDeposit
              ? this.editedLease.securityDeposit.replace(/,/g, '')
              : '0',
          ),
          totalLeasePayment: parseFloat(
            this.editedLease.totalLeasePayment
              ? this.editedLease.totalLeasePayment.replace(/,/g, '')
              : '0',
          ),
          petFee: parseFloat(
            this.editedLease.petFee
              ? this.editedLease.petFee.replace(/,/g, '')
              : '0',
          ),
          petCount: parseInt(this.editedLease.petCount, 10) || 0,
          includesGas: !!this.editedLease.includesGas,
          includesElectric: !!this.editedLease.includesElectric,
          includesWaterSewer: !!this.editedLease.includesWaterSewer,
          includesTrash: !!this.editedLease.includesTrash,
          includesCableTV: !!this.editedLease.includesCableTV,
          includesPhone: !!this.editedLease.includesPhone,
          includesWalkSnowRemoval: !!this.editedLease.includesWalkSnowRemoval,
          includesParkingSnowRemoval:
            !!this.editedLease.includesParkingSnowRemoval,
          includesLawncare: !!this.editedLease.includesLawncare,
          earlyResign: !!this.editedLease.earlyResign,
          maintenanceCode: this.editedLease.maintenanceCode || '',
          buildingCode: this.editedLease.buildingCode || '',
          garageCode: this.editedLease.garageCode || '',
          apartmentCode: this.editedLease.apartmentCode || '',
          // New additional services fields
          hasParking: !!this.editedLease.hasParking,
          parkingSpot: this.editedLease.parkingSpot || '',
          parkingFee: parseFloat(
            this.editedLease.parkingFee
              ? this.editedLease.parkingFee.replace(/,/g, '')
              : '0',
          ),
          parkingStart: this.editedLease.parkingStartRaw
            ? moment(this.editedLease.parkingStartRaw).format('YYYY-MM-DD')
            : null,
          hasStorage: !!this.editedLease.hasStorage,
          storageDesc: this.editedLease.storageDesc || '',
          storageFee: parseFloat(
            this.editedLease.storageFee
              ? this.editedLease.storageFee.replace(/,/g, '')
              : '0',
          ),
          storageStart: this.editedLease.storageStartRaw
            ? moment(this.editedLease.storageStartRaw).format('YYYY-MM-DD')
            : null,
          hasMobileAC: !!this.editedLease.hasMobileAC,
          mobileACModel: this.editedLease.mobileACModel || '',
          mobileACRent: parseFloat(
            this.editedLease.mobileACRent
              ? this.editedLease.mobileACRent.replace(/,/g, '')
              : '0',
          ),
          mobileACStart: this.editedLease.mobileACStartRaw
            ? moment(this.editedLease.mobileACStartRaw).format('YYYY-MM-DD')
            : null,
          dogCount: parseInt(this.editedLease.dogCount, 10) || 0,
          catCount: parseInt(this.editedLease.catCount, 10) || 0,
          petNames: this.editedLease.petNames || '',
          dogTemperament: this.editedLease.dogTemperament || 0,
          catTemperament: this.editedLease.catTemperament || 0,
        };

        if (this.editedLease.id) {
          // Update existing lease
          await axios.post('/api/update-lease', formattedLease);
        } else {
          // Create new lease
          await axios.post(
            '/api/create-lease',
            formattedLease,
            this.selectedProperty.id,
          );
        }

        this.fetchLeases(this.selectedProperty.id);
        this.closeEditLeaseDialog();
      } catch (error) {
        console.error('Error updating or creating lease:', error);
      }
    },

    addnewLease() {
      this.editedLease = {
        id: null, // Ensure ID is null to indicate a new lease
        //signedOnRaw: moment().format('YYYY-MM-DD'),
        //startsOnRaw: moment().format('YYYY-MM-DD'),
        //checklistDateRaw: moment().format('YYYY-MM-DD'),
        // endsOnRaw: moment().add(1, 'year').format('YYYY-MM-DD'), // Example: 1 year lease by default
        signedOnRaw: null,
        startsOnRaw: null,
        checklistDateRaw: null,
        endsOnRaw: null,
        secDepPaidOnRaw: null,
        balance: '0.00',
        remaining: '0.00',
        monthlyBaseRent: '0.00',
        securityDeposit: '0.00',
        totalLeasePayment: '0.00',
        petFee: '0.00',
        petCount: 0,
        includesGas: false,
        includesElectric: false,
        includesWaterSewer: false,
        includesTrash: false,
        includesPhone: false,
        includesCableTV: false,
        includesWalkSnowRemoval: false,
        includesParkingSnowRemoval: false,
        includesLawncare: false,
        earlyResign: false,
        apartmentCode: '',
        maintenanceCode: '',
        buildingCode: '',
        garageCode: '',
        // New additional services fields
        hasParking: false,
        parkingSpot: '',
        parkingFee: '0.00',
        parkingStartRaw: null,
        hasStorage: false,
        storageDesc: '',
        storageFee: '0.00',
        storageStartRaw: null,
        hasMobileAC: false,
        mobileACModel: '',
        mobileACRent: '0.00',
        mobileACStartRaw: null,
        dogCount: 0,
        catCount: 0,
        petNames: '',
        dogTemperament: 0,
        catTemperament: 0,
      };
      this.showEditLeaseDialog = true;
    },

    temperamentColor(value) {
      const colors = ['#aaa', '#4caf50', '#ffc107', '#f44336'];
      return colors[value] || colors[0];
    },

    temperamentLabel(value) {
      const labels = ['N/A', 'Friendly', 'Caution', 'Unfriendly'];
      return labels[value] || labels[0];
    },

    cycleTemperament(field) {
      this.editedLease[field] = ((this.editedLease[field] || 0) + 1) % 4;
    },

    openNotesPanel(id, noteType) {
      this.showNotesPanel = false;
      this.$nextTick(() => {
        this.noteType = noteType;
        this.noteId = id;
        this.showNotesPanel = true;
      });
    },

    toggleDocumentDeleted(docId, isDeleted) {
      const newDeletedStatus = isDeleted ? 0 : 1;

      axios
        .put(`/api/documents/${docId}/toggle-deleted`, {
          deleted: newDeletedStatus,
        })
        .then(() => {
          // Find the document and update its deleted status
          const doc = this.matchingDocuments.find((d) => d.Id === docId);
          if (doc) {
            doc.deleted = newDeletedStatus;
          }
        })
        .catch((error) => {
          console.error('Error toggling document deleted status:', error);
        });
    },

    checkOverflow() {
      const docContainer = this.$refs.docContainer;
      if (
        docContainer &&
        docContainer.scrollHeight > docContainer.clientHeight
      ) {
        this.isOverflowing = true;
      } else {
        this.isOverflowing = false;
      }
    },

    dragStart(event) {
      const element = event.target;
      this.dragOffset.x = event.clientX - element.getBoundingClientRect().left;
      this.dragOffset.y = event.clientY - element.getBoundingClientRect().top;
    },

    dragEnd(event) {
      const element = event.target;

      const left = event.clientX - this.dragOffset.x;
      const top = event.clientY - this.dragOffset.y;

      const parent = element.offsetParent || document.body;
      const parentWidth = parent.clientWidth;
      const parentHeight = parent.clientHeight;

      const newLeft = Math.min(
        Math.max(left, 0),
        parentWidth - element.clientWidth,
      );
      const newTop = Math.min(
        Math.max(top, 0),
        parentHeight - element.clientHeight,
      );

      element.style.left = `${newLeft}px`;
      element.style.top = `${newTop}px`;
    },

    // link lease document
    linkLeaseDoc() {
      if (!this.selectedDocument) {
        this.showDialog('Please select a document to link first.', ['Cancel']);
        return;
      }

      if (this.selectedLease.documentId) {
        // Lease is already linked to a document
        this.showDialog(
          'This lease is already linked to a document.<br>Do you want to unlink it?',
          ['Confirm', 'Cancel'],
        ).then((result) => {
          if (result === 'Confirm') {
            this.updateLeaseDocument(null);
            this.selectedDocument = null;
          }
        });
      } else {
        // No document linked, prompt to link the selected document
        this.showDialog(
          `You are linking:<br><br><strong>${this.selectedDocument.origDocName}</strong><br>to this lease.<br><br>`,
          ['Confirm', 'Cancel'],
        ).then((result) => {
          if (result === 'Confirm') {
            this.updateLeaseDocument(this.selectedDocument.Id);
          }
        });
      }
    },

    updateLeaseDocument(documentId) {
      axios
        .post('/api/link-lease-document', {
          leaseId: this.selectedLease.id,
          documentId: documentId,
        })
        .then(() => {
          // console.log('Lease document link updated successfully:', response.data);
          this.fetchLeases(this.selectedProperty.id); // Refresh the leases to reflect the change
        })
        .catch((error) => {
          console.error('Error updating lease document link:', error);
        });
    },

    showDialog(message, options) {
      this.dialogMessage = message;
      this.dialogOptions = options;
      this.showDialogPopup = true;

      return new Promise((resolve) => {
        this.dialogCallback = resolve;
      });
    },

    onDialogOptionSelected(option) {
      this.showDialogPopup = false;
      if (this.dialogCallback) {
        this.dialogCallback(option);
        this.dialogCallback = null; // Clear the callback
      }
    },

    onSearchTenantQuery() {
      if (this.tenantSearchQuery.trim() === '') {
        this.tenantSearchResults = [];
        return;
      }

      axios
        .get('/api/search-people', {
          params: {
            query: this.tenantSearchQuery,
            leaseId: this.selectedLease.id,
          },
        })

        .then((response) => {
          this.tenantSearchResults = response.data;
        })
        .catch((error) => {
          console.error('Error fetching tenants:', error);
        });
    },

    addSelectedTenantToLease(person) {
      this.showDialog(
        `You are adding:<br><br><strong>${person.fullName}</strong><br>to this lease.<br><br>`,
        ['Confirm', 'Cancel'],
      ).then((result) => {
        if (result === 'Confirm') {
          axios
            .post('/api/add-tenant-to-lease', {
              leaseId: this.selectedLease.id,
              peopleId: person.id,
            })
            .then(() => {
              // console.log('Tenant added successfully:', response.data);
              this.tenantSearchResults = this.tenantSearchResults.filter(
                (p) => p.id !== person.id,
              );
              this.tenantSearchQuery = '';
              this.fetchTenants(this.selectedLease.id);
              if (this.$refs.tenantSearchQuery)
                this.$refs.tenantSearchQuery.focus();
            })
            .catch((error) => {
              console.error('Error adding tenant to lease:', error);
            });
        }
      });
    },

    closeTenantSearchDialog() {
      this.tenantSearchQuery = '';
      this.showTenantSearchDialog = false;
      this.fetchTenants(this.selectedLease.id);
    },

    openPersonEditor(person = null) {
      // Explicit null (not click event): New Contact passes null for add mode.
      const isPerson =
        person && typeof person === 'object' && person.id != null;
      this.personToEdit = isPerson ? person : null;
      this.personEditorQuery = isPerson ? person.fullName || '' : '';
      this.personEditorStartAdd = !isPerson;
      this.showPersonEditor = true;
    },

    onPersonEditorClosed() {
      this.showPersonEditor = false;
      this.personToEdit = null;
      this.personEditorQuery = '';
      this.personEditorStartAdd = false;
      if (this.selectedLease) {
        this.fetchTenants(this.selectedLease.id);
      }
      if (this.showTenantSearchDialog) {
        this.onSearchTenantQuery();
      }
    },

    removeTenantFromLease() {
      // unlink this tenant
      this.showDialog(
        `You are removing:<br><br><strong>${this.selectedTenant.fullName}</strong><br>from this lease.<br><br>`,
        ['Confirm', 'Cancel'],
      ).then((result) => {
        if (result === 'Confirm') {
          axios
            .post('/api/remove-tenant-from-lease', {
              leaseId: this.selectedLease.id,
              peopleId: this.selectedTenant.id,
            })
            .then(() => {
              this.fetchTenants(this.selectedLease.id);
            })
            .catch((error) => {
              console.error('Error removing tenant from lease:', error);
            });
        }
      });
    },

    // table interaction methods
    toggleRowSelection(paymentId) {
      if (this.selectedPaymentIds.includes(paymentId)) {
        // If already selected, remove it
        this.selectedPaymentIds = this.selectedPaymentIds.filter(
          (id) => id !== paymentId,
        );
      } else {
        // If not selected, add it
        this.selectedPaymentIds.push(paymentId);
      }
      return;
    },

    confirmLinkPayment(paymentId) {
      if (!this.selectedLease) {
        this.showDialog('Please select a lease to link first.', ['Cancel']);
        return;
      }

      this.selectedPaymentId = paymentId;
      if (this.selectedPaymentIds.includes(paymentId)) {
        // its already linked; so unlink it (the API toggles it)
        this.showDialog(
          'Unlink payment from the selected Lease?<br/>(spacebar unlinks and autoselects the next)',
          ['Confirm', 'Cancel'],
        ).then((result) => {
          if (result === 'Confirm') {
            this.toggleRowSelection(paymentId);
            this.linkPaymentToLease(paymentId);
          } else {
            this.selectedPaymentId = null;
          }
        });
      } else {
        // it isn't linked yet, so link it. (The API toggles it)
        this.showDialog(
          'Link payment to the selected Lease?<br/>(spacebar links and autoselects the next)',
          ['Confirm', 'Cancel'],
        ).then((result) => {
          if (result === 'Confirm') {
            this.linkPaymentToLease(paymentId);
          } else {
            this.selectedPaymentId = null;
          }
        });
      }
    },

    editPaymentNote(paymentId) {
      this.selectedPaymentId = paymentId;
      this.fetchPaymentNote(paymentId);
      this.showPaymentNoteDialog = true;
    },

    fetchPaymentNote(paymentId) {
      axios
        .get('/api/fetch-payment-note', { params: { paymentId } })
        .then((response) => {
          this.paymentNote = response.data.note || '';
        })
        .catch((error) => {
          console.error('Error fetching payment note:', error);
        });
    },

    savePaymentNote() {
      axios
        .post('/api/save-payment-note', {
          paymentId: this.selectedPaymentId,
          note: this.paymentNote,
        })
        .then(() => {
          this.showPaymentNoteDialog = false;
          this.fetchTenantPayments(this.selectedTenant.id); // Refresh payments data
        })
        .catch((error) => {
          console.error('Error saving payment note:', error);
        });
    },

    linkPaymentToLease(paymentId) {
      // this is a toggle for link/unlink both

      // Step 1: Store the original paymentId
      const originalId = paymentId;

      axios
        .post('/api/update-lease-payment', {
          paymentId,
          leaseId: this.selectedLease.id,
        })
        .then(() => {
          // Step 2: Fetch the tenant payments
          return this.fetchTenantPayments(this.selectedTenant.id); // Refresh payments data
        })
        .then(() => {
          // Step 3: Find the index of originalId in tenantPayments
          const index = this.tenantPayments.findIndex(
            (payment) => payment.id === originalId,
          );

          // Step 4: Find the new payment ID at index + 1 (if it exists)
          if (index !== -1 && index + 1 < this.tenantPayments.length) {
            const newPaymentId = this.tenantPayments[index + 1].id;

            // Step 5: Call confirmLinkPayment with the new payment ID
            this.selectedPaymentId = newPaymentId;
            this.confirmLinkPayment(newPaymentId);
          } else {
            console.warn(
              'Could not find newPaymentId for confirmation - check the index and payment list.',
            );
          }

          // Refresh the ledger or update the UI as needed
          this.showLedger(this.selectedProperty, this.selectedLease);
        })
        .catch((error) => {
          console.error('Error linking payment to lease:', error);
        });
    },

    fitPdfToWidth(iframe) {
      iframe.onload = function () {
        const pdfDocument = iframe.contentWindow.document;
        const scaleSelect = pdfDocument.querySelector('select#zoom');
        if (scaleSelect) {
          scaleSelect.value = 'page-width';
          scaleSelect.dispatchEvent(new Event('change'));
        }
      };
    },

    handleLeaseClick(lease) {
      // Clear any existing timeout to handle the case where double-click occurs
      clearTimeout(this.leaseClickTimeout);

      // Set a timeout to delay the single-click action slightly
      this.leaseClickTimeout = setTimeout(() => {
        this.selectLease(lease);
      }, 500); // 200ms delay; adjust as needed
    },

    handleClick(person) {
      // Check if a click timeout is already set and clear it if so
      if (this.clickTimeout) {
        clearTimeout(this.clickTimeout);
        this.clickTimeout = null;
      }

      // Set a new timeout for the click handler
      this.clickTimeout = setTimeout(() => {
        this.addSelectedTenantToLease(person);
        this.clickTimeout = null; // Reset the timeout variable after execution
      }, 250); // 250 milliseconds delay to differentiate between click and dblclick
    },

    handleDblClick(person) {
      // Clear the click timeout to prevent the single-click handler from firing
      if (this.clickTimeout) {
        clearTimeout(this.clickTimeout);
        this.clickTimeout = null; // Reset the timeout variable
      }

      this.selectedPerson = person;
      this.openPersonEditor(person);
    },

    showFooterMessage(message) {
      // Set the footer message
      eventBus.emit('footerMessage', message);
    },

    async showLedger(property, lease) {
      this.recalcLedger = true;
      if (!property || !lease) return;

      this.ledgerData = [];

      this.ledgerSummary = {
        property: property.display || '', // assuming property.display is the address or identifier
        tenant: null,
        securityDeposit: lease.securityDeposit ? lease.securityDeposit : '',
        startsOn: lease.startsOn || '',
        endsOn: lease.endsOn || '',
        monthlyBaseRent: lease.monthlyBaseRent || '',
        prorated: lease.prorated || '0.00',
        paidOn: lease.secDepPaidOn || '',
      };

      // let initialBalance = lease.totalLeasePayment - (lease.prorated || 0);
      // 1. Add Opening Balance (fixed runningBalance)
      this.ledgerData.push({
        date: new Date(lease.startsOn),
        description: 'Opening Balance',
        credit: '',
        debit: lease.totalLeasePayment,
        num: '',
        depositGroup: null,
        type: '', // No type for opening balance
        runningBalance: lease.totalLeasePayment, // This is fixed for the opening balance
        expectedBalance: lease.totalLeasePayment,
        netBalance: 0,
        note: '',
        docId: '',
      });

      // 2. Fetch required data from the APIs
      const leasePayments = await this.fetchLeasePayments_ll(lease.id);
      const leaseLedger = await this.fetchLeaseLedger_ll(lease.id);

      await this.fetchTenants(lease.id); // Existing API for tenants
      const tenantNames = this.tenants
        .map((tenant) => tenant.fullName)
        .join(', '); // Map and join to get a comma-separated list of names
      this.ledgerSummary.tenant = tenantNames; // Set the tenant field to the formatted string

      const paymentTypeMap = {
        1: 'Check',
        2: 'E-Check',
        3: 'Cashiers',
        4: 'Other',
      };

      // 3. Add Prorated Rent if applicable
      if (lease.prorated !== 0 && lease.prorated !== null) {
        // Find all prorated payments (purpose === 2)
        const proratedPayments = leasePayments.filter(
          (payment) => payment.purpose === 2,
        );

        // Add a ledger entry for each prorated payment
        proratedPayments.forEach((proratedPayment) => {
          const proratedType = proratedPayment
            ? paymentTypeMap[proratedPayment.method]
            : '';

          // Use effectiveDate if available, otherwise fall back to paidOn
          const effectiveDate = proratedPayment.effectiveDate
            ? proratedPayment.effectiveDate
            : proratedPayment.paidOn;

          this.ledgerData.push({
            date: new Date(effectiveDate),
            description: 'Prorated Rent pmt',
            credit: proratedPayment.amount,
            debit: '',
            num: proratedPayment ? proratedPayment.transactionNumber : '',
            depositGroup: proratedPayment ? proratedPayment.depositGroup : null,
            type: proratedType,
            runningBalance: null, // This will be updated after sorting
            expectedBalance: null, // Will be updated later
            netBalance: null, // To be updated later
            note: proratedPayment ? proratedPayment.note : '',
            docId: proratedPayment ? proratedPayment.docId : '',
            ledgerId: null,
            paymentId: proratedPayment.id,
            paidOn: proratedPayment.paidOn,
          });
        });
      }

      // 4. Add Actual Payments and Ledger Entries (excluding prorated payments)
      leasePayments
        .filter((payment) => payment.purpose !== 2) // Exclude prorated payments
        .forEach((payment) => {
          const paymentType = paymentTypeMap[payment.method];

          const amount = payment.amount;
          const effectiveDate = payment.effectiveDate
            ? payment.effectiveDate
            : payment.paidOn;

          let description = 'Payment';
          let credit = amount;
          let debit = '';

          // Check if effectiveDate differs from paidOn and set description accordingly
          if (
            payment.effectiveDate &&
            payment.effectiveDate !== '' &&
            payment.paidOn !== payment.effectiveDate
          ) {
            description = `Payment on ${this.formatDate(
              payment.paidOn,
            )} effective ${this.formatDate(payment.effectiveDate)}`;
          }

          if (amount < 0) {
            description = 'Conv. fee';
            credit = '';
            debit = -amount;
          }

          this.ledgerData.push({
            date: new Date(effectiveDate),
            description: description,
            credit: credit,
            debit: debit,
            num: payment.transactionNumber,
            depositGroup: payment.depositGroup,
            type: paymentType,
            runningBalance: null, // To be updated later
            expectedBalance: 0, // Compute expected balance for all records except opening balance
            netBalance: null, // To be updated later
            note: payment.note,
            docId: payment.docId,
            ledgerId: null,
            paymentId: payment.id,
            paidOn: payment.paidOn,
          });
        });

      const ledgerTypeMap = {
        0: 'Other',
        1: 'Fee',
        2: 'Credit',
        3: 'Refund',
        4: 'Writeoff',
      };

      leaseLedger.forEach((ledgerEntry) => {
        this.ledgerData.push({
          date: ledgerEntry.date,
          description: ledgerEntry.description,
          credit: ledgerEntry.credit || '',
          debit: ledgerEntry.debit || '',
          num: ledgerEntry.TransactionNum,
          depositGroup: null,
          type: ledgerTypeMap[ledgerEntry.type],
          runningBalance: null, // To be updated later
          expectedBalance: lease.totalLeasePayment, // Compute expected balance for all records except opening balance
          netBalance: null,
          note: ledgerEntry.note || '',
          docId: '',
          ledgerId: ledgerEntry.id,
          paymentId: '',
          account: ledgerEntry.account || null,
        });
      });

      // 5. Add Expected Rent Payments
      const startsOnDate = new Date(lease.startsOn); // Ensure lease.startsOn is a Date object
      const endMonth = new Date(lease.endsOn); // Convert endsOn to a Date object if it's a string
      // Initialize the currentMonth as the start of the period
      let currentMonth =
        parseFloat(lease.prorated) !== 0 && lease.prorated
          ? new Date(
              startsOnDate.getFullYear(),
              startsOnDate.getMonth(),
              startsOnDate.getDate(),
            ) // Case 1: Start on lease.startsOn date
          : new Date(startsOnDate.getFullYear(), startsOnDate.getMonth(), 1); // Cases 2 and 3: Start on 1st of the month

      // Loop through each month from startMonth to endMonth (inclusive)
      // Initialize monthIndex to 0
      let monthIndex = 0;

      // Loop through each month from startMonth to endMonth (inclusive)
      while (
        currentMonth.getFullYear() < endMonth.getFullYear() ||
        (currentMonth.getFullYear() === endMonth.getFullYear() &&
          currentMonth.getMonth() <= endMonth.getMonth())
      ) {
        const monthStartDate = new Date(currentMonth); // Use the first day of the current month as date

        // Case 1: Prorated payment exists
        if (parseFloat(lease.prorated) !== 0 && lease.prorated) {
          if (monthIndex === 0) {
            // The first payment (prorated)
            this.ledgerData.push({
              date: monthStartDate,
              description: 'Expected Prorated Pmt',
              credit: '',
              debit: '',
              num: '',
              depositGroup: null,
              type: '',
              runningBalance: null, // Will be updated later
              expectedBalance: lease.totalLeasePayment - lease.prorated,
              netBalance: null, // To be calculated later
              note: '',
              docId: '',
            });
          } else {
            // Subsequent payments
            this.ledgerData.push({
              date: monthStartDate,
              description: this.getExpectedRentPmtLabel(monthStartDate, lease),
              credit: '',
              debit: '',
              num: '',
              depositGroup: null,
              type: '',
              runningBalance: null, // Will be updated later
              expectedBalance:
                lease.totalLeasePayment -
                this.getExpectedDeductionsThrough(lease, monthStartDate),
              netBalance: null, // To be calculated later
              note: '',
              docId: '',
            });
          }
        }

        // Case 2: No prorated rent, but startsOn is not the first of the month
        else if (
          (lease.prorated === null || parseFloat(lease.prorated) === 0) &&
          startsOnDate.getDate() !== 1
        ) {
          // The first payment starts from the next month
          if (monthIndex > 0) {
            this.ledgerData.push({
              date: monthStartDate,
              description: this.getExpectedRentPmtLabel(monthStartDate, lease),
              credit: '',
              debit: '',
              num: '',
              depositGroup: null,
              type: '',
              runningBalance: null, // Will be updated later
              expectedBalance:
                lease.totalLeasePayment -
                this.getExpectedDeductionsThrough(lease, monthStartDate),
              netBalance: null, // To be calculated later
              note: '',
              docId: '',
            });
          }
        }
        // Case 3: No prorated rent, and startsOn is the 1st of the month
        else if (
          (lease.prorated === null || parseFloat(lease.prorated) === 0) &&
          startsOnDate.getDate() === 1
        ) {
          // Payments start on the first of the month
          this.ledgerData.push({
            date: monthStartDate,
            description: this.getExpectedRentPmtLabel(monthStartDate, lease),
            credit: '',
            debit: '',
            num: '',
            depositGroup: null,
            type: '',
            runningBalance: null, // Will be updated later
            expectedBalance:
              lease.totalLeasePayment -
              this.getExpectedDeductionsThrough(lease, monthStartDate),
            netBalance: null, // To be calculated later
            note: '',
            docId: '',
          });
        }

        // Move to the next month
        currentMonth.setMonth(currentMonth.getMonth() + 1);
        currentMonth.setDate(1);

        // Increment monthIndex
        monthIndex++;
      }

      // 6. Sort by date in ascending order
      this.ledgerData.sort((a, b) => {
        // Extract only the date (ignore time)
        const dateA = new Date(a.date).setHours(0, 0, 0, 0); // Set time to 00:00:00
        const dateB = new Date(b.date).setHours(0, 0, 0, 0); // Set time to 00:00:00

        const dateDiff = dateA - dateB;
        // If dates are the same, prioritize as follows:
        // 1. Opening Balance
        // 2. Expected Rent Pmt
        // 3. Other transactions
        if (dateDiff === 0) {
          if (
            a.description === 'Opening Balance' &&
            b.description !== 'Opening Balance'
          ) {
            return -1; // Sort 'a' before 'b'
          }
          if (
            a.description !== 'Opening Balance' &&
            b.description === 'Opening Balance'
          ) {
            return 1; // Sort 'b' before 'a'
          }
          if (
            a.description === 'Expected Prorate Pmt' &&
            b.description !== 'Expected Prorate Pmt'
          ) {
            return -1; // Sort 'a' before 'b'
          }
          if (
            a.description !== 'Expected Prorate Pmt' &&
            b.description === 'Expected Prorate Pmt'
          ) {
            return 1; // Sort 'b' before 'a'
          }
          const isExpectedRentPmt = (description) =>
            isExpectedRentDescription(description);
          if (
            isExpectedRentPmt(a.description) &&
            !isExpectedRentPmt(b.description)
          ) {
            return -1; // Sort 'a' before 'b'
          }
          if (
            !isExpectedRentPmt(a.description) &&
            isExpectedRentPmt(b.description)
          ) {
            return 1; // Sort 'b' before 'a'
          }
        }

        // If dates are different, sort by date
        return dateDiff;
      });

      // 7. Calculate runningBalance and other balance fields
      this.ledgerData.forEach((entry, index) => {
        if (index === 0) {
          // The first entry is the opening balance, so we don't update it
          return;
        }

        const previousEntry = this.ledgerData[index - 1];

        // Update runningBalance
        const previousBalance = parseFloat(previousEntry.runningBalance || 0);
        const credit = parseFloat(entry.credit || 0);
        const debit = parseFloat(entry.debit || 0);
        entry.runningBalance = previousBalance + debit - credit;

        const entryDate = new Date(entry.date);
        entry.expectedBalance =
          lease.totalLeasePayment -
          this.getExpectedDeductionsThrough(lease, entryDate);

        // Determine if netBalance should be meaningful or blank
        const currentMonth = new Date();
        if (entryDate <= currentMonth) {
          // Calculate netBalance only for months up to and including the current month
          entry.netBalance = entry.expectedBalance - entry.runningBalance;
          // Safely update the balance only if selectedLease exists
          if (this.selectedLease) {
            this.selectedLease.balance = entry.netBalance;
          }
        } else {
          // Set netBalance as blank for future months
          entry.netBalance = '';
        }
      });

      // update the balance for the selected lease
      if (this.selectedLease && this.selectedLease.balance !== undefined) {
        const { id, balance } = this.selectedLease;
        axios
          .put('/api/update-lease-balance/', { id, balance })
          .catch((error) => {
            console.error(
              'Error updating balance:',
              error.response ? error.response.data.error : error.message,
            );
            // Handle error appropriately, maybe show a message to the user
          });
      }
      this.recalcLedger = false;
    },

    // Axios API calls
    async fetchLeasePayments_ll(leaseId) {
      try {
        const response = await axios.get(
          `/api/lease-payments?leaseId=${leaseId}`,
        );
        return response.data;
      } catch (error) {
        console.error('Error fetching lease payments:', error);
        throw error;
      }
    },

    async fetchLeaseLedger_ll(leaseId) {
      try {
        const response = await axios.get(
          `/api/lease-ledger?leaseId=${leaseId}`,
        );
        return response.data;
      } catch (error) {
        console.error('Error fetching lease ledger:', error);
        throw error;
      }
    },

    // for ledger
    getColumnClass(colIndex) {
      if (colIndex === 0) return 'left-align';
      if (colIndex === 4) return 'center-align';
      if (colIndex === 5) return 'center-align';
      if (colIndex === 1) return 'left-align wide-col';
      if (colIndex === 2 || colIndex === 3 || (colIndex >= 7 && colIndex <= 9))
        return 'right-align';
      return 'center-align';
    },

    updateShowNotesPanel(newVal) {
      this.showNotesPanel = newVal;
    },

    editLedgerRow(index) {
      this.showSubForm = false;
      this.addNewLedgerEntry = false;

      if (index != null) {
        this.selectedLedgerRow = index;
      }
      this.showLedgerForm = !this.showLedgerForm;

      let row = this.ledgerData[this.selectedLedgerRow];
      this.isLeaseLedgerRecord = false;
      this.isLeasePaymentRecord = false;

      if ((row && row.ledgerId) || this.addNewLedgerEntry) {
        this.isLeaseLedgerRecord = true;
      }
      if (row && row.paymentId && !this.addNewLedgerEntry) {
        this.isLeasePaymentRecord = true;
      }
    },

    // captures the contents of a DIV to a JPEG and then clipboard
    async captureImage(elementName) {
      const element = document.getElementById(elementName); // The area to capture
      try {
        const canvas = await html2canvas(element); // Capture the area as a canvas

        // copy it here, for emailing
        this.capturedImage = canvas.toDataURL('image/jpeg', 1.0);

        // Convert canvas to a Blob
        canvas.toBlob(async (blob) => {
          try {
            // Copy the blob to the clipboard
            await navigator.clipboard.write([
              new ClipboardItem({ 'image/png': blob }),
            ]);
            this.$refs.shutterSound.play();
          } catch (error) {
            console.error('Failed to copy image to clipboard:', error);
          }
        });
      } catch (error) {
        console.error('Error capturing image:', error);
      }
    },

    inlineScopedCSS(element) {
      // inject styles
      return (
        `
      <style>
          /* Base styles */
          body { padding: 10px; font-family: Consolas; box-sizing: border-box; }
          #ledgerBody { margin-top: 5px; font-size: 0.9em; margin-bottom: 20px; }

          /* Ledger summary styling with table-based layout for better PDF rendering */
          #ledgerBody .ledger-summary {
            background-color: #f2f2f2;
            padding: 10px;
            border-bottom: 2px solid #000;
            font-size: 0.9em;
            width: calc(100% - 5px) !important; /* Subtract padding to match table width */
            box-sizing: border-box !important;
            margin: 0 auto !important;
          }

          /* Title section with absolute positioning for better PDF rendering */
          #ledgerBody .ledger-title {
            position: relative;
            font-size: 1.3em;
            font-weight: bold;
            margin-top: 5px;
            margin-bottom: 10px;
            width: 100%;
            height: 30px;
            clear: both;
          }

          #ledgerBody .ledger-title > span:first-child {
            position: absolute;
            left: 0;
            top: 0;
            text-align: left;
          }

          #ledgerBody .ledger-title > span:last-child {
            position: absolute;
            right: 0;
            top: 0;
            background-color: #aaa;
            color: white;
            text-align: right;
            padding: 4px 8px;
            border-radius: 3px;
          }

          /* Explicit balance indicator styles for PDF rendering */
          .balance-indicator {
            position: absolute !important;
            right: 0 !important;
            top: 0 !important;
            background-color: #aaa !important;
            color: white !important;
            text-align: right !important;
            padding: 4px 8px !important;
            border-radius: 3px !important;
            display: inline-block !important;
            margin: 0 !important;
          }

          /* Summary rows with table-like structure for better PDF compatibility */
          #ledgerBody .summary-row {
            display: table !important;
            table-layout: fixed !important;
            width: 100% !important;
            margin-bottom: 5px !important;
          }

          #ledgerBody .summary-row.full-width {
            width: 100% !important;
          }

          #ledgerBody .summary-item {
            display: table-cell !important;
            vertical-align: middle !important;
            padding: 2px !important;
          }

          #ledgerBody .summary-item:first-child {
            width: 40% !important;
          }

          #ledgerBody .summary-item[style*="justify-content: right"] {
            text-align: right !important.
          }

          #ledgerBody .summary-item label {
            font-weight: normal !important;
            margin-right: 5px !important;
            display: inline-block !important;
          }

          #ledgerBody .summary-item strong,
          #ledgerBody .summary-item a {
            white-space: nowrap !important;
            display: inline-block !important;
          }

          /* Table styling - unchanged but with !important for PDF generation */
          #ledgerBody .transaction-table {
            width: 100% !important;
            border-collapse: collapse !important;
            margin-top: 10px !important;
            font-size: 11px !important;
            box-sizing: border-box !important;
          }
          #ledgerBody .transaction-table th, #ledgerBody .transaction-table td { border: 1px solid #aaa !important; padding: 5px !important; }
          #ledgerBody .transaction-table th { background-color: #bbbbbb !important; text-align: left !important; vertical-align: top !important; }
          #ledgerBody .transaction-table td.left-align, #ledgerBody .transaction-table th.left-align { text-align: left !important; }
          #ledgerBody .transaction-table td.right-align, #ledgerBody .transaction-table th.right-align { text-align: right !important; }
          #ledgerBody .transaction-table td.center-align, #ledgerBody .transaction-table th.center-align { text-align: center !important; }
          #ledgerBody .transaction-table tbody tr:nth-child(even) { background-color: #ccc !important; }
          #ledgerBody .footer-summary { text-align: right !important; font-weight: bold !important; }
          .no-start-date { margin-top: 20px !important; }
          .selected-row { background-color: #907793 !important; color: #fff !important; }
          .ledger-howto {
            margin-top: 10px !important;
            font-size: 0.75em !important;
            font-style: italic !important;
            padding: 5px !important;
            background: white !important;
            color: #111 !important;
            page-break-inside: avoid !important;
            width: 100% !important;
            box-sizing: border-box !important;
          }

          /* Wrapper to align all content */
          .transaction-table-outer, .transaction-table-inner {
            width: 100% !important;
            box-sizing: border-box !important;
          }
          </style>
  ` + element
      );
    },

    async printLedger(elementName, mode = 'print') {
      this.selectedLedgerRow = null;
      this.isLedgerPrinting = true; // Disable the print button and show spinner
      try {
        const element = document.getElementById(elementName);
        if (!element) {
          console.error('Element not found:', elementName);
          this.isLedgerPrinting = false; // Re-enable the button if element not found
          return null; // Return null if element is not found
        }

        // Clone the element to avoid modifying the original HTML
        const clonedElement = element.cloneNode(true);

        // Replace FontAwesome icons with Unicode characters for PDF rendering
        clonedElement.querySelectorAll('.fa-check').forEach((icon) => {
          const span = document.createElement('span');
          span.textContent = '✓';
          span.style.color = 'green';
          span.style.fontSize = '14px';
          span.style.fontWeight = 'bold';
          if (icon.title) span.title = icon.title;
          // Set parent div to center align
          let parent = icon.parentElement;
          while (parent && parent.tagName !== 'TD') {
            parent.style.textAlign = 'center';
            parent = parent.parentElement;
          }
          if (parent && parent.tagName === 'TD') {
            parent.style.textAlign = 'center';
          }
          icon.replaceWith(span);
        });
        clonedElement.querySelectorAll('.fa-times').forEach((icon) => {
          const span = document.createElement('span');
          span.textContent = '✗';
          span.style.color = 'red';
          span.style.fontSize = '14px';
          span.style.fontWeight = 'bold';
          if (icon.title) span.title = icon.title;
          // Set parent div to center align
          let parent = icon.parentElement;
          while (parent && parent.tagName !== 'TD') {
            parent.style.textAlign = 'center';
            parent = parent.parentElement;
          }
          if (parent && parent.tagName === 'TD') {
            parent.style.textAlign = 'center';
          }
          icon.replaceWith(span);
        });
        clonedElement.querySelectorAll('.fa-question').forEach((icon) => {
          const span = document.createElement('span');
          span.textContent = '?';
          span.style.color = 'orange';
          span.style.fontSize = '14px';
          span.style.fontWeight = 'bold';
          if (icon.title) span.title = icon.title;
          // Set parent div to center align
          let parent = icon.parentElement;
          while (parent && parent.tagName !== 'TD') {
            parent.style.textAlign = 'center';
            parent = parent.parentElement;
          }
          if (parent && parent.tagName === 'TD') {
            parent.style.textAlign = 'center';
          }
          icon.replaceWith(span);
        });

        // Select all rows in the table and hide the last two columns
        clonedElement.querySelectorAll('tr').forEach((row) => {
          const cells = row.querySelectorAll('td, th');
          if (cells.length >= 2) {
            cells[1].style.width = '130px';
            cells[4].style.width = '110px';
            cells[5].style.width = '70px';
            cells[5].style.textAlign = 'center'; // Center align the S column
            // Also center align any divs inside cell 5 and remove width constraint
            const divsInCell5 = cells[5].querySelectorAll('div');
            divsInCell5.forEach((div) => {
              div.style.width = 'auto';
              div.style.textAlign = 'center';
            });
          }
          if (cells.length >= 2) {
            cells[cells.length - 1].style.display = 'none';
            cells[cells.length - 2].style.display = 'none';
            cells[cells.length - 4].style.display = 'none';
            cells[cells.length - 5].style.display = 'none';
          }
        });

        const htmlContentWithCSS = this.inlineScopedCSS(
          clonedElement.outerHTML,
        );

        const response = await axios.post(
          '/api/convert-to-pdf',
          { html: htmlContentWithCSS },
          { responseType: 'blob' },
        );

        // Create a Blob from the response data
        const pdfBlob = new Blob([response.data], { type: 'application/pdf' });

        if (mode === 'print') {
          const url = URL.createObjectURL(pdfBlob);

          const iframe = document.createElement('iframe');
          iframe.style.display = 'none';
          iframe.src = url;
          document.body.appendChild(iframe);
          this.isLedgerPrinting = false; // Re-enable the button after PDF has been generated

          iframe.onload = () => {
            if (iframe.contentWindow) {
              iframe.contentWindow.print();
              setTimeout(() => {
                if (document.body.contains(iframe)) {
                  document.body.removeChild(iframe);
                  URL.revokeObjectURL(url);
                  this.showFooterMessage(
                    'Removed print preview after 10 seconds.',
                  );
                }
              }, 20000); // Adjust delay as needed
            }
          };
        } else if (mode === 'email') {
          this.isLedgerPrinting = false; // Re-enable the button
          return pdfBlob; // Return the Blob for email attachment
        }
      } catch (error) {
        this.isLedgerPrinting = false; // Re-enable the button in case of error
        console.error('Error generating PDF:', error);
        return null; // Return null if there was an error
      }
    },

    // new methods for editing ledger rows
    setFormType(type) {
      this.formMode = type;
      this.ledgerErrorMessage = '';
      let row = this.ledgerData[this.selectedLedgerRow];
      this.isLeaseLedgerRecord = false;
      this.isLeasePaymentRecord = false;
      if ((row && row.ledgerId) || this.addNewLedgerEntry) {
        this.isLeaseLedgerRecord = true;
      }
      if (row && row.paymentId && !this.addNewLedgerEntry) {
        this.isLeasePaymentRecord = true;
      }
      if (type === 'add') {
        this.form = {};
        this.form.date = new Date().toISOString().slice(0, 10);
        this.form.type = null;
        this.form.account = null;
        this.ledgerErrorMessage = '';
      } else if (type === 'edit') {
        if (this.isLeasePaymentRecord) {
          this.ledgerErrorMessage =
            'You can only set an effective date for a payment.';
          this.showSubForm = false;
          this.showEffectiveDate = true;
          const paidOnFormatted = row?.paidOn
            ? moment(row.paidOn).format('YYYY-MM-DD')
            : null;
          const rowDateFormatted = row?.date
            ? moment(row.date).format('YYYY-MM-DD')
            : null;
          if (
            paidOnFormatted &&
            rowDateFormatted &&
            paidOnFormatted !== rowDateFormatted
          ) {
            this.form.date = rowDateFormatted;
          } else {
            const startsOn =
              this.selectedLease?.startsOn || this.ledgerSummary?.startsOn;
            this.form.date = startsOn
              ? moment(startsOn).format('YYYY-MM-DD')
              : rowDateFormatted || moment().format('YYYY-MM-DD');
          }
          return;
        }
        if (this.ledgerData[this.selectedLedgerRow].ledgerId) {
          const reverseLedgerTypeMap = {
            Other: 0,
            Fee: 1,
            Credit: 2,
            Refund: 3,
          };
          this.form.date = new Date(row.date).toISOString().slice(0, 10);
          this.form.credit = row.credit;
          this.form.debit = row.debit;
          this.form.num = row.num;
          this.form.description = row.description;
          this.form.type = reverseLedgerTypeMap[row.type];
          this.form.account = row.account || null;
        } else {
          // its a payment row
        }
      }
      this.showLedgerForm = true;
      this.showSubForm = true;
    },

    closeLedgerForm() {
      this.showLedgerForm = false;
      this.showSubForm = false;
      this.ledgerErrorMessage = '';
      this.form = {}; // Reset form data
      this.form.date = new Date().toISOString().slice(0, 10); // Default to today's date
    },

    async submitEffectiveDate() {
      if (this.formMode === 'edit') {
        // Ensure this is a lease payment record and validate the date
        if (this.isLeasePaymentRecord) {
          if (!this.form.date || isNaN(Date.parse(this.form.date))) {
            this.ledgerErrorMessage = 'Please enter a valid date.';
            return;
          } else {
            // Convert the effective date to the format 'YYYY-MM-DD HH:MM:SS'
            const effectiveDate = new Date(this.form.date)
              .toISOString()
              .slice(0, 19)
              .replace('T', ' ');

            try {
              // Await the API call to set the effective date
              const response = await axios.post('/api/update-effective-date', {
                paymentId: this.ledgerData[this.selectedLedgerRow].paymentId,
                effectiveDate: effectiveDate, // Send formatted date
              });

              // Handle success response from the API
              this.ledgerErrorMessage = response.data.message; // Display the success message
              this.showLedger(this.selectedProperty, this.selectedLease); // Refresh the ledger or update the UI as needed
            } catch (error) {
              // Handle error response
              this.ledgerErrorMessage =
                'Error updating effective date: ' + error.response.data.error;
            }
          }
        } else {
          this.ledgerErrorMessage =
            'Can only modify effective dates for lease payments.';
          return;
        }
      }
    },

    submitLedgerForm() {
      if (this.formMode === 'add' || this.formMode === 'edit') {
        if (this.isLeaseLedgerRecord) {
          // Validate Lease Ledger Form
          if (!this.form.date || isNaN(Date.parse(this.form.date))) {
            this.ledgerErrorMessage = 'Please enter a valid date.';
            return;
          }
          if (!this.form.description || this.form.description.trim() === '') {
            this.ledgerErrorMessage = 'Description is required.';
            return;
          }
          if (this.form.credit != null && this.form.credit < 0) {
            this.ledgerErrorMessage = 'Credit must be a non-negative number.';
            return;
          }
          if (this.form.debit != null && this.form.debit < 0) {
            this.ledgerErrorMessage = 'Debit must be a non-negative number.';
            return;
          }
          if (this.form.credit == null && this.form.debit == null) {
            this.ledgerErrorMessage =
              'Either credit or debit must be a non-negative number.';
            return;
          }
          if (this.form.type === 'x' || this.form.type === null) {
            this.ledgerErrorMessage = 'Please select a valid type.';
            return;
          }

          // If all validations pass, continue to add the lease ledger
          if (this.formMode === 'add') this.addLeaseLedger();
          if (this.formMode === 'edit') this.updateLeaseLedger();
          this.showLedgerForm = false;
          this.addNewLedgerEntry = false;
        } else {
          // Validate Lease Payment Form
          // Similar validation logic here for lease payment
          this.addLeasePayment();
        }
      }
    },

    addLeaseLedger() {
      axios
        .post('/api/lease-ledger/add', {
          ...this.form,
          leaseId: this.selectedLease.id,
        })
        .then(() => {
          this.showLedger(this.selectedProperty, this.selectedLease); // Refresh data
          this.closeLedgerForm();
        })
        .catch((error) => console.error('Error adding lease ledger:', error));
    },

    updateLeaseLedger() {
      axios
        .post('/api/lease-ledger/update', {
          ...this.form,
          leaseId: this.selectedLease.id,
          ledgerId: this.ledgerData[this.selectedLedgerRow].ledgerId,
        })
        .then(() => {
          this.showLedger(this.selectedProperty, this.selectedLease); // Refresh data
          this.closeLedgerForm();
        })
        .catch((error) => console.error('Error updating lease ledger:', error));
    },

    addLeasePayment() {
      axios
        .post('/api/lease-payment/add', this.form)
        .then(() => {
          this.showLedger(this.selectedProperty, this.selectedLease); // Refresh data
          this.closeLedgerForm();
        })
        .catch((error) => console.error('Error adding lease payment:', error));
    },

    editLeaseLedger() {
      axios
        .post('/api/lease-ledger/edit', this.form)
        .then(() => {
          this.showLedger(this.selectedProperty, this.selectedLease); // Refresh data
          this.closeLedgerForm();
        })
        .catch((error) => console.error('Error editing lease ledger:', error));
    },

    editLeasePayment() {
      axios
        .post('/api/lease-payment/edit', this.form)
        .then(() => {
          this.showLedger(this.selectedProperty, this.selectedLease); // Refresh data
          this.closeLedgerForm();
        })
        .catch((error) => console.error('Error editing lease payment:', error));
    },

    confirmDeleteLedgerEntry() {
      if (this.isLeaseLedgerRecord) {
        this.deleteLeaseLedger();
      } else {
        alert(
          'Cannot delete lease payment. Only lease ledger entries can be deleted.',
        );
      }
    },

    deleteLeaseLedger() {
      axios
        .post('/api/lease-ledger/delete', {
          id: this.ledgerData[this.selectedLedgerRow].ledgerId,
        })
        .then(() => {
          this.showLedger(this.selectedProperty, this.selectedLease); // Refresh data
          this.closeLedgerForm();
        })
        .catch((error) => console.error('Error deleting lease ledger:', error));
    },

    rejectPayment() {
      const row = this.ledgerData[this.selectedLedgerRow];
      if (!row || !row.paymentId) {
        this.ledgerErrorMessage = 'Please select a payment row to reject.';
        return;
      }
      const amount = parseFloat(row.credit) || 0;
      if (amount <= 0) {
        this.ledgerErrorMessage =
          'Cannot reject: payment has no positive credit amount.';
        return;
      }
      const date = new Date(row.date).toISOString().slice(0, 10);
      const transNum = row.num ? String(row.num) : '';
      const offsetNum = transNum ? transNum + '-v' : '';
      axios
        .post('/api/lease-ledger/add', {
          date: date,
          description: 'offset for rejected DEX payment',
          credit: 0,
          debit: amount,
          num: offsetNum,
          type: 0,
          account: null,
          leaseId: this.selectedLease.id,
        })
        .then(() => {
          this.showLedger(this.selectedProperty, this.selectedLease);
          this.closeLedgerForm();
        })
        .catch((error) => {
          console.error('Error rejecting payment:', error);
          this.ledgerErrorMessage = 'Error creating offset entry.';
        });
    },

    // for email feature
    async fetchRecipients() {
      try {
        const response = await axios.get(
          `/api/people/${this.selectedLease.id}`,
        );

        this.recipientsArray = leaseSendAddresses(response.data);
        this.recipients = this.recipientsArray.join(', ');
        // console.log('Recipients:', this.recipients);
      } catch (error) {
        console.error('Error fetching recipients:', error);
      }
    },

    setToDan() {
      const danEmail = 'dan@brandenburgcapitalmanagement.com';
      const propertyName =
        this.selectedProperty?.display ||
        this.ledgerSummary?.property ||
        'property';
      this.recipientsArray = [danEmail];
      this.recipients = danEmail;
      this.subject = `Ledger for : ${propertyName}`;
      this.emailBodyTop = `Dear Dan, ledger for ${propertyName} is attached as PDF.`;
      this.emailBodyBottom = '';
      this.updatePreview();
    },

    async sendEmail() {
      this.selectedLedgerRow = null;
      this.showEmailComposer = false;
      try {
        // Generate the PDF blob using printLedger in 'email' mode
        const pdfBlob = await this.printLedger('ledgerBody', 'email'); // Replace 'ledgerElementId' with the actual ID of the element

        if (!pdfBlob) {
          throw new Error('Failed to generate PDF attachment.');
        }

        // Create a FormData object for sending the email with an attachment
        const formData = new FormData();
        formData.append('subject', this.subject);
        formData.append('body', this.composeEmailPreview);
        // formData.append('recipients', this.recipientsArray.join(',')); // Assuming recipientsArray is an array of email addresses
        formData.append('recipients', this.recipientsArray); // Assuming recipientsArray is an array of email addresses
        formData.append('attachment', pdfBlob, 'ledger.pdf'); // Attach the PDF blob with a filename

        // Send the email with the PDF attachment
        await axios.post('/api/send-email', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        // Handle success response
        this.resetEmailForm(); // Clear the form after sending
        this.showEmailComposer = false;
        this.showFooterMessage('sent email to recipients');
      } catch (error) {
        console.error('Error sending email:', error);
        this.showFooterMessage('Failed to send email. Please try again.');
      }
    },

    printComposeEmailPreview() {
      const bodyHtml = marked.parse(this.emailBodyTop + this.emailBodyBottom);
      const toLine =
        this.recipientsArray && this.recipientsArray.length > 0
          ? this.recipientsArray.join('; ')
          : this.recipients || '';
      const fromLine = 'communications@bigpropertymanagement.com';
      const subjectLine = this.subject || '';

      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        this.showFooterMessage(
          'Pop-up blocked. Please allow pop-ups for print preview.',
        );
        return;
      }

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Print Preview - Compose Email</title>
          <style>
            @page {
              size: auto;
              margin: 0.65in;
            }
            body {
              font-family: Arial, Helvetica, sans-serif;
              color: #222;
              font-size: 12pt;
              line-height: 1.45;
            }
            .header-row {
              margin-bottom: 8px;
              word-break: break-word;
            }
            .header-row strong {
              display: inline-block;
              min-width: 72px;
            }
            hr {
              border: none;
              border-top: 1px solid #bbb;
              margin: 14px 0;
            }
            h1, h2, h3, h4, h5, h6 {
              margin: 0.65em 0 0.35em;
            }
            p {
              margin: 0.45em 0;
            }
            ul, ol {
              margin: 0.45em 0;
              padding-left: 1.4em;
            }
            code {
              background: #f3f3f3;
              padding: 2px 4px;
              border-radius: 3px;
              font-family: Consolas, 'Courier New', monospace;
            }
            pre {
              background: #f3f3f3;
              padding: 10px;
              border-radius: 4px;
              overflow-x: auto;
            }
            table {
              border-collapse: collapse;
              width: 100%;
              margin: 0.6em 0;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 6px;
              text-align: left;
            }
            blockquote {
              margin: 0.7em 0;
              padding-left: 12px;
              border-left: 3px solid #ccc;
              color: #555;
            }
          </style>
        </head>
        <body>
          <div class="header-row"><strong>To:</strong> ${toLine}</div>
          <div class="header-row"><strong>From:</strong> ${fromLine}</div>
          <div class="header-row"><strong>Subject:</strong> ${subjectLine}</div>
          <hr />
          <div>${bodyHtml}</div>
        </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
    },

    resetEmailForm() {
      // Reset the form fields and hide the email composer
      this.subject = '';
      this.emailBodyTop = '';
      this.emailBodyBottom = '';
      this.capturedImage = '';
      this.showEmailComposer = false;
    },

    captureImageforEmail() {
      // this.captureImage('ledgerBody');
      this.fetchRecipients();
      this.subject = 'Your Pixie Properties ledger is attached';
      this.showEmailComposer = true; // Show the email composition section
      this.updatePreview();
    },

    closeComposer() {
      this.showEmailComposer = false;
    },

    formatCurrency(value) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    },

    deleteLease() {
      // Confirm deletion
      this.showDialog('Are you sure you want to delete this lease?', [
        'Confirm',
        'Cancel',
      ]).then((result) => {
        if (result === 'Confirm') {
          axios
            .post('/api/delete-lease', {
              leaseId: this.selectedLease.id,
            })
            .then(() => {
              this.showEditLeaseDialog = false;
              this.fetchLeases(this.selectedProperty.id); // Refresh the leases to reflect the change
            })
            .catch((error) => {
              console.error('Error deleting lease:', error);
              this.showDialog('Error deleting lease', ['OK']).then(
                (dialogResult) => {
                  if (dialogResult === 'OK') {
                    this.showEditLeaseDialog = false;
                  }
                },
              );
            });
        }
      });
    },

    selectCheck(docId) {
      this.showPDF = true;
      this.viewerTitle = 'Check Viewer';

      try {
        // Fetch the PDF directly from the backend
        axios
          .get(`/api/load-pdf/${docId}`, {
            responseType: 'arraybuffer',
          })
          .then((response) => {
            if (response.status === 200) {
              // Create a Blob from the response data
              const pdfBlob = new Blob([response.data], {
                type: 'application/pdf',
              });
              const pdfUrl = URL.createObjectURL(pdfBlob);

              // Set the URL for your iframe or download link
              this.pdfUrl = pdfUrl;
              this.showFooterMessage(`PDF for document ID ${docId} loaded.`);
            } else {
              console.error(
                `Failed to load PDF for document ID ${docId}. Status: ${response.status}`,
              );
              this.pdfUrl = '';
              this.showFooterMessage('Failed to load PDF: Server error.');
            }
          })
          .catch((error) => {
            console.error('Error loading PDF:', error);
            this.pdfUrl = '';
            this.showFooterMessage('Failed to load PDF.');
          });
      } catch (error) {
        console.error('Unexpected error:', error);
        this.showFooterMessage('Failed to load PDF.');
      }
    },

    async setNewLeaseDefaults() {
      // Set the lease name from the selected document
      this.selectedDocument
        ? (this.editedLease.name = this.selectedDocument.Title)
        : '';

      // Extract date range from filename if it exists
      if (this.selectedDocument && this.selectedDocument.Title) {
        const datePattern = /(\d{6})\s*-\s*(\d{6})/;
        const match = this.selectedDocument.Title.match(datePattern);

        if (match) {
          const startDateStr = match[1]; // MMDDYY format
          const endDateStr = match[2]; // MMDDYY format

          try {
            // Convert MMDDYY to YYYY-MM-DD format
            const startDate = this.convertMMDDYYToDate(startDateStr);
            const endDate = this.convertMMDDYYToDate(endDateStr);

            if (startDate && endDate) {
              this.editedLease.startsOnRaw = startDate;
              this.editedLease.endsOnRaw = endDate;
              // Set checklist date to the same as start date
              this.editedLease.checklistRaw = startDate;
            }
          } catch (error) {
            // Silently continue if date parsing fails
            console.log('Date parsing failed:', error);
          }
        }
      }

      // Fetch previous lease data and copy include* values
      if (this.selectedProperty && this.selectedProperty.id) {
        try {
          const response = await axios.get(
            `/api/previous-lease/${this.selectedProperty.id}`,
          );
          if (response.data && response.data.length > 0) {
            const previousLease = response.data[0];

            // Copy include* checkbox values from previous lease
            this.editedLease.includesGas = !!previousLease.includesGas;
            this.editedLease.includesElectric =
              !!previousLease.includesElectric;
            this.editedLease.includesWaterSewer =
              !!previousLease.includesWaterSewer;
            this.editedLease.includesTrash = !!previousLease.includesTrash;
            this.editedLease.includesCableTV = !!previousLease.includesCableTV;
            this.editedLease.includesPhone = !!previousLease.includesPhone;
            this.editedLease.includesWalkSnowRemoval =
              !!previousLease.includesWalkSnowRemoval;
            this.editedLease.includesParkingSnowRemoval =
              !!previousLease.includesParkingSnowRemoval;
            this.editedLease.includesLawncare =
              !!previousLease.includesLawncare;
          }
        } catch (error) {
          // Silently continue if no previous lease is found
          console.log('No previous lease found or error fetching:', error);
        }
      }
    },

    convertMMDDYYToDate(mmddyy) {
      if (!mmddyy || mmddyy.length !== 6) {
        return null;
      }

      const month = mmddyy.substring(0, 2);
      const day = mmddyy.substring(2, 4);
      const year = mmddyy.substring(4, 6);

      // Convert YY to YYYY (assume 20XX for years 00-99)
      const fullYear = parseInt(year) < 50 ? `20${year}` : `19${year}`;

      // Return in YYYY-MM-DD format for date inputs
      return `${fullYear}-${month}-${day}`;
    },

    setDefaultTitle() {
      this.selectedDocument
        ? (this.editedLease.name = this.selectedDocument.Title)
        : '';
    },

    isValidDate(date) {
      const parsedDate = new Date(date);
      return !isNaN(parsedDate.getTime());
    },

    updateChecklistDate() {
      if (
        this.isValidDate(this.editedLease.startsOnRaw) &&
        !this.editedLease.checklistRaw
      ) {
        this.editedLease.checklistRaw = this.editedLease.startsOnRaw;
      }
    },

    handleLedgerRowClick(event, rowIndex) {
      if (event.ctrlKey || event.metaKey) {
        const newSet = new Set(this.selectedLedgerRows);
        if (newSet.has(rowIndex)) {
          newSet.delete(rowIndex);
        } else {
          newSet.add(rowIndex);
        }
        this.selectedLedgerRows = newSet;
      } else {
        this.selectedLedgerRows = new Set();
      }
      this.selectedLedgerRow = rowIndex;
    },

    handleKeyDown(event) {
      // If the Escape key is pressed
      if (event.key === 'Escape') {
        if (this.isMouseOverLedger && this.selectedLedgerRows.size > 0) {
          this.selectedLedgerRows = new Set();
          return;
        }
        if (this.selectedLedgerRow != null) {
          this.selectedLedgerRow = null;
          this.showEditPopup = false;
          return;
        }
        if (
          this.searchQuery === '' &&
          this.$route.query.search &&
          this.firstTimeSearched
        ) {
          this.searchQuery = this.$route.query.search;
          this.firstTimeSearched = false;
          this.onSearch(); // Trigger search when component is created
          return;
        }
        this.searchQuery = '';
        if (this.$refs.searchQuery) this.$refs.searchQuery.focus();
        this.properties = [];
        this.selectedProperty = null;
        this.selectedProperty = null;
        this.selectedDocument = null;
        this.selectedLease = null;
        this.selectedTenant = null;
      }
    },

    async openLedgerPopup() {
      try {
        // Fetch and display the updated ledger list without adding a new entry
        await this.fetchLedgerList();
        this.showLedgerListPopup = true;
      } catch (error) {
        console.error('Failed to fetch ledger list:', error);
      }
    },

    async fetchLedgerList() {
      try {
        const response = await axios.get('/api/fetch-ledgerlist');
        this.ledgerList = response.data;
      } catch (error) {
        console.error('Failed to fetch ledger list:', error);
      }
    },

    // Add the currently viewed lease/ledger to the watch list
    async addToList() {
      this.isAdding = true;
      if (!this.selectedDocument) {
        this.showDialog(
          'You must have a lease with a valid linked lease document first.',
          ['OK'],
        ).then(() => {
          this.isAdding = false;
          return;
        });
        return;
      }

      // This flow generates PDFs, uploads many chunks, merges, and inserts a
      // large BLOB — far beyond the global 20s axios timeout.
      const longTimeout = 180000;

      try {
        // Call printLedger and capture the generated Blob
        const docuBlob = await this.printLedger('ledgerBody', 'email'); // Ensure printLedger returns a Blob

        if (!(docuBlob instanceof Blob)) {
          throw new Error('Failed to generate PDF blob for ledger attachment.');
        }

        // Prepare tenant information
        const tenantNames =
          this.selectedLease.peopleEmails
            .map((person) => person.fullName)
            .join(', ') + '\n& All Other Occupants';
        const tenantInfo = `${tenantNames} \n${
          this.selectedProperty.StreetNumber
        } ${this.selectedProperty.StreetName} ${
          this.selectedProperty.StreetType
        } ${this.selectedProperty.StreetDir} ${
          this.selectedProperty.UnitNumber === 'House'
            ? '\n'
            : `\n Unit # ${this.selectedProperty.UnitNumber}\n`
        }Grand Rapids, MI ${this.selectedProperty.Zipcode}`;

        // Prepare the demand letter data
        const demandLetterData = {
          tenant: tenantInfo,
          landlord: 'Pixie Properties LLC, c/o Big Property Management',
          rent: this.formatRent(
            this.demandBalanceOverride !== 0
              ? this.demandBalanceOverride
              : -this.selectedLease.balance,
          ),
          premises: '',
          ndate: new Date().toLocaleDateString('en-US'),
          laddress: '315 Richard Terrace SE',
          lcity: 'Grand Rapids, MI 49506',
          ltelephone: '(616) 916-6465',
        };

        // Request to generate the demand letter PDF
        const demandLetterResponse = await axios.post(
          '/api/fill-demand-letter',
          demandLetterData,
          {
            responseType: 'arraybuffer',
            timeout: longTimeout,
          },
        );
        const demandLetterPdfBlob = new Blob([demandLetterResponse.data], {
          type: 'application/pdf',
        });

        // Fetch the lease PDF bytes
        const docId =
          typeof this.selectedDocument === 'object' &&
          this.selectedDocument !== null
            ? this.selectedDocument.Id
            : this.selectedDocument;

        const leaseResponse = await axios.get(`/api/load-pdf/${docId}`, {
          responseType: 'arraybuffer',
          timeout: longTimeout,
        });
        const leasePdfBlob = new Blob([leaseResponse.data], {
          type: 'application/pdf',
        });

        // Stream each PDF up in <1MB chunks (bypasses nginx's 1MB body limit),
        // then ask the server to merge by uploadId.
        const chunkOpts = { timeout: longTimeout };
        const [docuId, demandId, leaseId] = await Promise.all([
          uploadChunked(docuBlob, chunkOpts),
          uploadChunked(demandLetterPdfBlob, chunkOpts),
          uploadChunked(leasePdfBlob, chunkOpts),
        ]);

        const mergeResponse = await axios.post(
          '/api/merge-pdfs',
          { uploadIds: [docuId, demandId, leaseId] },
          { responseType: 'blob', timeout: longTimeout },
        );

        const combinedDocBlob = mergeResponse.data;

        // Create docTitle from property and current date
        const currentDate = new Date()
          .toISOString()
          .replace('T', ' ')
          .slice(0, 16);
        const docTitle = `${this.ledgerSummary.property}-${currentDate}`;

        // Stream the merged blob up via chunked upload too, then submit a tiny
        // multipart form referencing its uploadId.
        const mergedUploadId = await uploadChunked(combinedDocBlob, chunkOpts);
        const formData = new FormData();
        formData.append('docTitle', docTitle);
        formData.append('uploadId', mergedUploadId);
        formData.append('leaseId', this.selectedLease.id || 0);
        // type 1 is "watch list" type
        formData.append('type', 1);
        formData.append('balanceOverride', this.demandBalanceOverride || 0);

        // Send to add-ledgerlist API (BLOB insert can be slow on DEV/Galera)
        await axios.post('/api/add-ledgerlist', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: longTimeout,
        });

        // Refresh the ledger list after adding the new entry
        await this.fetchLedgerList();
      } catch (error) {
        console.error('Failed to add to ledger list:', error);
        const timedOut =
          error?.code === 'ECONNABORTED' ||
          /timeout/i.test(error?.message || '');
        const msg = timedOut
          ? 'Adding to the ledger list timed out (PDF build/upload/save can take a while). Please try again.'
          : error?.response?.data?.error ||
            error?.message ||
            'Failed to add to ledger list.';
        if (typeof this.showDialog === 'function') {
          this.showDialog(msg, ['OK']);
        }
      } finally {
        this.isAdding = false;
      }
    },

    async removeLedgerItem(id) {
      try {
        // Remove the item with the specified id from this.ledgerList
        this.ledgerList = this.ledgerList.filter((item) => item.id !== id);

        // Proceed to make the API call
        await axios.delete(`/api/remove-ledgerlist/${id}`);

        // Optionally, refresh the list after deletion
        // if you want to sync with the server-side data again
        await this.fetchLedgerList();
      } catch (error) {
        console.error('Failed to remove ledger item:', error);
        // Optionally, handle the case where the API call fails
        // by re-adding the item back to the list or notifying the user
      }
    },

    async replaceItem(item) {
      // Store the balance override value from the item being replaced
      const preservedBalanceOverride = item.balanceOverride || 0;

      // Remove the current item
      await this.removeLedgerItem(item.id);

      // Set up for replacement
      this.demandBalanceOverride = preservedBalanceOverride;
      this.replacingItem = true;

      // Search for the property/lease
      this.listClickSearch(item);

      // Wait a bit for the search to complete and then try to add automatically
      setTimeout(async () => {
        if (
          this.replacingItem &&
          this.ledgerData &&
          this.ledgerData.length > 0
        ) {
          try {
            await this.addToList();
            this.replacingItem = false;
          } catch (error) {
            console.error('Error during item replacement:', error);
            this.replacingItem = false;
          }
        }
      }, 1000); // Give enough time for search and ledger calculation
    },

    async viewLedgerItem(id) {
      try {
        const response = await axios.get(`/api/fetch-ledgerlist-blob/${id}`, {
          responseType: 'blob', // Ensure Axios gets a Blob for the PDF
        });

        // Create a Blob URL with the correct MIME type
        const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
        this.pdfUrl = URL.createObjectURL(pdfBlob); // Set the Blob URL for the iframe
        this.showPDF = true;
        this.viewerTitle = 'Ledger Item';
      } catch (error) {
        console.error('Failed to view ledger item:', error);
      }
    },

    closeLedgerList() {
      this.showLedgerListPopup = false;
    },

    startPopupDrag(event, popupRefName) {
      const popupEl = this.$refs[popupRefName];
      if (!popupEl) {
        return;
      }

      const popupRect = popupEl.getBoundingClientRect();
      popupEl.style.left = `${popupRect.left}px`;
      popupEl.style.top = `${popupRect.top}px`;

      this.isPopupDragging = true;
      this.activeDraggedPopup = popupEl;
      this.popupDragOffset.x = event.clientX - popupRect.left;
      this.popupDragOffset.y = event.clientY - popupRect.top;
      document.body.style.userSelect = 'none';
    },

    handlePopupDrag(event) {
      if (!this.isPopupDragging || !this.activeDraggedPopup) {
        return;
      }

      this.activeDraggedPopup.style.left = `${
        event.clientX - this.popupDragOffset.x
      }px`;
      this.activeDraggedPopup.style.top = `${
        event.clientY - this.popupDragOffset.y
      }px`;
    },

    stopPopupDrag() {
      if (!this.isPopupDragging) {
        return;
      }

      this.isPopupDragging = false;
      this.activeDraggedPopup = null;
      document.body.style.userSelect = '';
    },

    toggleDragMode(event) {
      // Set drag mode to true and identify the targeted icon and popup container
      this.isDragMode = !this.isDragMode;

      if (this.isDragMode) {
        // Capture the icon clicked and determine its closest popup container
        const iconElement = event.target;
        const popupContainer = iconElement.closest('.draggable-popup'); // Generalize by class

        if (popupContainer) {
          this.currentPopup = popupContainer;

          // Get bounding rectangles to calculate the offset
          const iconRect = iconElement.getBoundingClientRect();
          const popupRect = popupContainer.getBoundingClientRect();

          // Calculate offset between the icon and the popup's top-left corner
          this.currentIconOffset.x = iconRect.left - popupRect.left;
          this.currentIconOffset.y = iconRect.top - popupRect.top;

          // Add a one-time event listener to capture the next click location
          document.addEventListener('click', this.setPopupPosition);
        }
      } else {
        // If drag mode is off, remove the listener
        document.removeEventListener('click', this.setPopupPosition);
      }
    },

    setPopupPosition(event) {
      // Ensure there is a popup to position
      if (this.currentPopup) {
        // Move the popup to align the icon with the new click location
        this.popupPosition.x = event.clientX - this.currentIconOffset.x;
        this.popupPosition.y = event.clientY - this.currentIconOffset.y;

        // Apply inline styles dynamically to the popup's position
        this.currentPopup.style.left = `${this.popupPosition.x}px`;
        this.currentPopup.style.top = `${this.popupPosition.y}px`;

        // Reset drag mode and remove event listener
        this.isDragMode = false;
        document.removeEventListener('click', this.setPopupPosition);
      }
    },

    bringToFront(event, parentClass = 'draggable-popup') {
      // Find the closest parent with the specified class
      const parentElement = event.target.closest(`.${parentClass}`);
      if (parentElement) {
        // Get the highest z-index currently in use on the page
        const allElements = Array.from(document.querySelectorAll('*'));
        const maxZIndex = allElements.reduce((max, el) => {
          const zIndex = parseFloat(window.getComputedStyle(el).zIndex);
          return isNaN(zIndex) ? max : Math.max(max, zIndex);
        }, 0);

        // Set the parent element's z-index to be one higher than the current highest
        parentElement.style.zIndex = maxZIndex + 1;
      }
    },

    openTenantEnvelope(tenant) {
      this.selectedTenant = tenant;
      const to = activeAddressFromPerson(tenant);
      if (!to) return;
      this.openEmailEditor(to);
    },

    openEmailEditor(
      to = 'dan@bigpropertymanagement.com',
      subject = '',
      body = '<br><br>**Pixie Properties Team**<br>616-916-6953',
    ) {
      eventBus.emit('setEmailDetails', { to, subject, body });
    },

    navigatePerson(tenant) {
      this.$router.push({
        path: '/people',
        query: { search: tenant.fullName },
      });
    },

    async fetchTempData() {
      if (!this.namePattern || !this.afterDate) {
        return;
      }

      let attempt = 0; // Track the number of attempts
      const maxAttempts = 2; // We will try at most twice
      let response;

      while (attempt < maxAttempts) {
        try {
          // Prepare the pattern for current attempt
          let thePattern = this.namePattern.replace(/ /g, '%') + '%Rent';

          // Remove '#' from the pattern in the second attempt
          if (attempt === 1 && this.namePattern.includes('#')) {
            thePattern = thePattern.replace(/#/g, '');
            this.namePattern = this.namePattern.replace(/#/g, '');
          }

          response = await axios.get(
            `/api/historicalLedger?namePattern=${encodeURIComponent(
              thePattern,
            )}&afterDate=${encodeURIComponent(this.afterDate)}`,
          );

          // Check if status is not OK
          if (response.status !== 200) {
            throw new Error(`Failed to fetch ledger: ${response.statusText}`);
          }

          // Check if data is empty
          if (response.data && response.data.length > 0) {
            // Set tempData and break out of the loop if data is found
            this.tempData = response.data;
            this.filteredTempData = this.tempData;
            break;
          }
        } catch (error) {
          console.error(error);
          break; // Break the loop if an error occurs during fetch
        }

        attempt++;
      }
    },

    setTempName() {
      if (!this.selectedProperty) return;
      this.namePattern = (this.selectedProperty.display || '')
        .replace('Apt ', '#')
        .replace('House', '')
        .replace('Unit', '');
      this.fetchTempData();
    },

    selectTempRow(index, row, event) {
      this.selectedTempRow = index;

      // Check if CTRL key is pressed
      if (event.ctrlKey) {
        // Filter filteredTempData where item.Name equals row.Name
        this.filteredTempData = this.filteredTempData.filter(
          (item) => item.Name === row.Name,
        );
      }
    },

    splitName(val) {
      if (typeof val !== 'string') {
        console.warn('Expected a string, but received:', val);
        return undefined;
      }

      const parts = val.trim().split(':');

      // If there are fewer than two parts, log a warning and handle accordingly.
      if (parts.length < 2) {
        console.warn(
          'The input string does not have enough segments to return the last two.',
        );
        return parts.join(' '); // Return whatever is available.
      }

      // Get the last two elements from the array
      const lastTwoParts = parts.slice(-2);

      return lastTwoParts.join(' ').replace('Lease', '');
    },
    updateDescription() {
      // If "Other..." is not selected, set description to selected predefined value
      if (this.selectedPredefined !== 'Other...') {
        this.form.description = this.selectedPredefined;
        if (this.selectedPredefined === 'Early Payment offer 5') {
          if (this.form.credit == null || this.form.credit === '') {
            this.form.credit = 5;
          }
          if (this.form.type === 'x' || this.form.type == null) {
            this.form.type = 2;
          }
        }
      } else {
        this.form.description = ''; // Clear when selecting "Other..."
      }
    },
    syncTempDataToLedger(row) {
      const foundIndex = this.ledgerData.findIndex((item) => {
        if (item && item.num) {
          // Check if item.num includes row.num
          if (item.num.toString().includes(row.Num.toString())) {
            return true;
          }
        } else {
          // Log a message if item or item.num is undefined or null
          console.error('Item or item.num is invalid:', item);
        }
        return false;
      });

      // If found, update the selectedLedgerRow with the index
      if (foundIndex !== -1) {
        this.selectedLedgerRow = foundIndex;
      } else {
        // Handle the case where the num is not found, if necessary
        this.selectedLedgerRow = null;
        this.addNewLedgerEntry = true;
        this.setFormType('add');
        this.form.num = row.Num;
        this.selectedPredefined = 'Manual Pmt entry';
        this.form.date = row.Date.slice(0, 10);
        this.form.credit = row.Type == 'Deposit' ? -row.Amount : 0;
        this.form.debit = row.Type == 'Invoice' ? row.Amount : 0;
        this.form.description = 'Manual Pmt Entry';
        this.form.type = 0;
      }
    },

    getAllPhoneNumbers(tenant) {
      // If tenant.phone is a string with multiple numbers separated by commas
      if (typeof tenant.phone === 'string' && tenant.phone.includes(',')) {
        return tenant.phone
          .split(',')
          .map((phone) => phone.trim())
          .join('\n');
      }
      // If it's just a single phone number or empty
      return tenant.phone || 'No phone number';
    },

    getFirstPhoneNumber(tenant) {
      // Return just the first phone number for display
      if (typeof tenant.phone === 'string' && tenant.phone.trim()) {
        return tenant.phone.split(',')[0].trim();
      }
      return tenant.phone || '';
    },
  },

  watch: {
    filteredProperties(value) {
      // console.log('watch: filteredProperties: value', value[0]);
      if (value && value.length == 1) {
        // console.log('4354: watch', value);
        this.selectProperty(value[0]);
      }
    },

    showEmailComposer(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          const emailInput = this.$refs.emailBodyTop;
          if (emailInput) {
            emailInput.focus();
            // Select from position 21 to the end of the content
            const startPosition = 21;
            const endPosition = emailInput.value.length; // The length of the input's value
            emailInput.setSelectionRange(startPosition, endPosition);
          }
        });
      }
    },

    showEditLeaseDialog(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          if (this.$refs.editTotalLeasePayment) {
            this.$refs.editTotalLeasePayment.focus();
            this.$refs.editTotalLeasePayment.select();
          }
        });
      }
    },

    recalcLedger(newVal, oldVal) {
      // Check if recalcLedger changed from true to false
      if (oldVal === true && newVal === false) {
        // Call addToList only when recalcLedger goes from true to false
        if (
          this.showLedgerListPopup &&
          this.replacingItem &&
          this.ledgerData.length > 0
        ) {
          this.$nextTick(() => {
            this.addToList();
            this.replacingItem = false;
          });
        }
      }
    },

    showSubForm(newVal) {
      if (newVal && this.isLeaseLedgerRecord) {
        this.$nextTick(() => {
          if (this.$refs.editLedgerDate) this.$refs.editLedgerDate.focus();
        });
      }
    },

    showTenantSearchDialog(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          if (this.$refs.tenantSearchQuery)
            this.$refs.tenantSearchQuery.focus();
        });
      }
    },

    pdfUrl(newUrl) {
      this.$nextTick(() => {
        const iframe = document.getElementById('pdf-frame');
        if (newUrl && iframe) iframe.src = newUrl;
      });
    },

    selectedLease(newVal) {
      this.showLedgerPanel = newVal ? true : false;
      //console.log('selected Lease: newVal=', newVal);
      this.showLedger(this.selectedProperty, newVal);
    },

    matchingDocuments() {
      this.$nextTick(() => {
        this.checkOverflow(); // Re-check overflow when documents change
      });
    },

    showDialogPopup(newValue) {
      if (newValue) {
        this.$nextTick(() => {
          const confirmButton = document.getElementById('confirmButton');
          if (confirmButton) {
            confirmButton.focus();
          }
        });
      }
    },
  },
};
</script>

<style scoped>
.lease-search-page {
  padding-left: 15px;
  width: 1150px;
  height: 95%;
  display: flex;
  flex-direction: row;
  position: relative;
  user-select: none;
}

.left-column {
  flex: 1;
  min-width: 515px;
}

.right-column {
  flex: 1;
}

.temp-column {
  flex: 1;
}

.temp-data {
  width: 334px;
  height: 645px;
  border: 1pt solid #eee;
  padding: 5px;
  border-radius: 4px;
  margin-left: 10px;
  margin-top: 155px;
}

/* search box section */
.search-bar {
  align-items: center;
  position: relative;
}

.label {
  font-size: 0.8em;
  font-weight: bold;
  margin-bottom: 3px;
}

.search-input {
  width: 457px;
  padding: 5px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.clear-button {
  padding: 5px 10px;
  background: #eee;
  border: 1px solid #ccc;
  border-radius: 4px;
  cursor: pointer;
  color: #333;
}

.content-wrapper {
  display: flex;
}

.property-list-wrapper {
  width: 250px;
  overflow: hidden;
}

.property-list {
  top: 100%;
  left: 0;
  right: 0;
  width: 265px;
  background-color: rgba(255, 255, 255, 0.1);
  border: 1px solid #ccc;
  border-radius: 4px;
  height: 123px;
  overflow-y: auto;
  z-index: 100;
}

.property-item {
  padding: 1px 5px;
  cursor: pointer;
  font-size: 0.8em;
}

.property-item.selected-property {
  background-color: #907793;
}

.selected-item {
  background-color: #907793;
}

/* lease document section */
.lease-doc-wrapper {
  margin-left: 5px;
  width: 244px;
  overflow: hidden;
  cursor: pointer;
}

.matching-leases {
  position: relative;
  height: 118px;
  overflow-y: hidden;
  background-color: #b4b3b3;
  width: 248px;
  overflow-x: hidden;
  border: 1px solid #ccc;
  padding-left: 4px;
  padding-bottom: 5px;
}

.doc-container {
  user-select: none;
  padding: 5px;
  height: 65px;
  overflow-y: scroll;
  overflow-x: hidden;
  width: 248px;
  font-size: 0.95em;
}

.document-item {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 95%; /* Ensure the document item takes full width */
  display: block; /* Ensure the document item behaves like a block-level element */
  font-size: 0.8em;
  position: relative;
  padding-right: 10px;
}

.toggle-eye {
  position: absolute;
  right: 0;
  cursor: pointer;
  color: #444;
  font-size: 0.8em;
  margin-top: 2px;
  margin-right: 2px;
}

.document-item:hover .toggle-eye {
  visibility: visible;
}

.document-item:hover {
  background-color: #baa2bd;
}

.more-arrow {
  position: absolute;
  bottom: 4px; /* Adjust as needed */
  right: 36px; /* Adjust as needed */
  font-size: 12px;
  color: #333; /* Or any color you prefer */
}

/* PDF viewer */
.pdf-viewer {
  /* border: 1px solid rgb(145, 146, 146); */
  height: 795px;
  width: 855px;
  overflow: hidden;
  margin-top: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pdf-viewer iframe {
  width: 100%;
  height: 100%;
}

/* Lease and Tenant Sections */
.lease-wrapper {
  max-height: 150px;
  overflow-y: auto;
  background: #b4b3b3;
  width: 496px;
  overflow-x: hidden;
  border: 1pt solid #ccc;
  padding: 2px;
  border-radius: 4px;
}

.lease-item {
  padding: 3px 3px;
  border-radius: 4px;
  cursor: pointer;
  margin-bottom: 2px;
  font-size: 0.75em;
  width: 490px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.lease-info {
  flex: 1;
}

.lease-icons {
  display: flex;
  gap: 3px;
  color: #333;
}

.tenant-item:hover {
  background: #baa2bd;
}

.selected-lease,
.selected-tenant {
  background-color: #907793;
}

.lease-and-payments,
.tenant-and-payments {
  justify-content: flex-start;
  margin-top: 20px;
}

.lease-section,
.tenant-section {
  width: 100%;
  user-select: none;
}

.tenant-section {
  border: 1pt solid #ccc;
  width: 494px;
  border-radius: 4px;
  padding: 3px;
}

.lease-payment-history,
.tenant-payment-history {
  margin-top: 10px;
  width: 500px;
}

/* Table Styles */
table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 5px;
  font-size: 0.8em;
  text-align: left;
}

table th,
table td {
  border: 1px solid #ddd;
  padding: 5px;
  font-size: 0.9em;
}

table th {
  background: #999;
}

.highlight-date {
  color: red;
  font-weight: bold;
}

:deep(.highlight) {
  background: #d6bbda;
}

tbody tr:nth-child(even) {
  background-color: #f2f2f2;
}

tbody tr:nth-child(odd) {
  background-color: #ffffff;
}

.tenant-payment-history-container {
  width: 500px;
  margin-top: 10px;
  overflow-x: hidden;
  overflow-y: hidden;
  padding-top: 20px;
}

.table-container {
  max-height: 340px;
  overflow-y: auto;
  overflow-x: hidden;
  width: 520px;
  margin-bottom: 25px;
}

.scrollable-tbody {
  display: block;
  max-height: 300px;
  overflow-y: scroll;
  overflow-x: hidden;
}

.tenant-payment-history-container thead {
  width: 480px;
  table-layout: fixed;
}

.tenant-payment-history-container tr {
  display: table;
  width: 499px;
  table-layout: fixed;
}

.tenant-payment-history-container tbody {
  display: table;
  width: 480px;
  table-layout: fixed;
}

.tenant-payment-history-container .odd-row {
  background-color: #f9f9f9;
}

.tenant-payment-history-container .table th.right-align,
.tenant-payment-history-container .table td.right-align {
  text-align: right;
  min-width: 75px;
}

.tenant-payment-history-container .table th.transact-num,
.tenant-payment-history-container .table td.transact-num {
  width: 80px;
  max-width: 80px;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tenant-payment-history-container .table th.narrow,
.table td.narrow {
  width: 60px;
}

.tenant-payment-history-container .table th.center-align,
.tenant-payment-history-container .table td.center-align {
  text-align: center;
  max-width: 50px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tenant-payment-history-container .table th.center-align-narrow,
.table td.center-align-narrow {
  text-align: center;
  width: 30px;
}

.tenant-payment-history-container .table th.wide-column,
.table td.wide-column {
  width: 130px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tenant-payment-history-container .table th.fixed-column,
.table td.fixed-column {
  min-width: 85px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.new-lease-doc,
.new-lease,
.new-tenant {
  font-size: 0.5em;
  font-weight: bold;
  margin-top: 5px;
  background: #7354775c;
  width: 20px;
  padding: 5px;
  color: #ddd;
  margin-bottom: 5px;
  border-radius: 4px;
}

.new-lease-doc:hover,
.new-lease:hover,
.new-tenant:hover {
  background-color: #baa2bd;
  cursor: pointer;
}

.new-lease,
.new-tenant {
  margin-left: 3px;
  margin-bottom: 4px;
}

.lease-doc-title {
  font-size: 0.8em;
  margin-top: 4px;
  padding-left: 3px;
  font-weight: bold;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 233px;
}

.lease-doc-title .left-align {
  text-align: left;
}

.lease-doc-title .right-align {
  text-align: right;
}

.lease-item:hover {
  background-color: #baa2bd;
  cursor: pointer;
}

.lease-wrapper i {
  margin-left: 6px;
}

.lease-item i,
.tenant-item .tenant-icons i {
  visibility: visible;
  opacity: 0.1;
  transition: opacity 0.5s;
}

/* Show the icons when hovering over the lease-item or tenant-item */
.lease-item:hover i,
.tenant-item:hover i {
  visibility: visible;
  opacity: 1;
  transition: opacity 0.2s;
}

.tenant-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 3px 3px;
  border-radius: 4px;
  cursor: pointer;
  margin-bottom: 2px;
  font-size: 0.8em;
  width: 490px;
}

.tenant-info {
  flex: 1;
}

.primary-tenant-name {
  font-weight: 700;
}

.tenant-icons {
  display: flex;
  gap: 5px;
}

.tenant-info a {
  margin-right: 10px;
  color: black;
}

/* for lease editing dialog */
.edit-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  height: 100%;
  width: 565px;
  background: rgba(63, 63, 63, 0.5);
  z-index: 1000;
}

.edit-dialog {
  position: fixed;
  top: 120px;
  left: 50px;
  background-color: #c9c9c9;
  border-radius: 8px;
  box-shadow: 8px 7px 10px 6px rgba(0, 0, 0, 0.1);
  z-index: 1001;
  width: 480px;
  padding: 10px;
  font-size: 0.8em;
  user-select: none;
}

.edit-dialog-content h2 {
  margin-top: -10px;
  font-size: 20px;
  text-align: center;
  background: #3b3b3b;
  height: 33px;
  border-radius: 5px 5px 0 0;
  padding-top: 10px;
  margin-left: -10px;
  margin-right: -10px;
  color: #ddd;
  font-size: 20px;
  text-align: center;
}

.edit-dialog-body {
  margin: 20px 0;
  width: 95%;
}

.edit-dialog-body label {
  display: block;
  font-weight: bold;
  margin-bottom: 5px;
}

.edit-dialog-body input[type='text'],
.edit-dialog-body input[type='number'],
.edit-dialog-body input[type='date'] {
  width: 100%; /* Adjusted width to be consistent for all inputs */
  padding: 4px;
  margin-bottom: 5px;
  border: 1px solid #9d9d9d;
  border-radius: 4px;
}

.edit-dialog-body .search-input {
  width: 170px !important;
  margin-left: 12px;
}

/* Style for Name Input */
.name-input {
  margin-bottom: 0px;
  margin-top: -14px;
}

.name-input label {
  display: block;
  font-weight: bold;
  margin-bottom: -5px;
}
.name-input button {
  margin-left: 11px;
  height: 26px;
  padding-top: -1px;
  /* margin-top: 27px; */
}

.name-input input[type='text'] {
  width: calc(100% - 88px); /* Ensure it aligns with other inputs */
  border: 1px solid #ccc;
  border-radius: 4px;
}

/* Layout for Date Fields */
.date-inputs,
.amount-inputs {
  display: flex;
  flex-wrap: wrap; /* Allows the items to wrap into multiple rows */
  justify-content: space-between;
  align-items: center;
  gap: 5px; /* Adds consistent spacing between fields */
  margin-top: 5px;
  margin-bottom: 7px;
}

.date-field,
.amount-field {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 22%; /* Both date fields and amount fields now have the same width */
  box-sizing: border-box; /* Ensures padding is included in the width */
}

.amount-field {
  width: 22%;
}

.date-field input[type='date'],
.amount-field input[type='text'],
.amount-field input[type='number'] {
  margin-top: 5px;
  width: 100%; /* Make input fields take the full width of the parent */
  padding: 3px;
}

/* Layout for Checkbox Group */
.checkbox-group {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  margin-bottom: 15px;
}

.checkbox-group label {
  font-size: 0.9em;
}

.checkbox-field {
  width: 31%; /* Adjusted to fit two checkboxes per row */
  display: flex;
  align-items: center;
}

.checkbox-field input[type='checkbox'] {
  margin-right: 10px;
}

/* Additional Services Group */
.additional-services-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 10px;
}

.service-row {
  display: grid;
  grid-template-columns: 120px minmax(90px, 1fr) minmax(90px, 1fr) 140px;
  align-items: center;
  gap: 8px;
}

.service-row .checkbox-field {
  width: 120px;
  flex-shrink: 0;
}

.service-input {
  flex: 1;
  min-width: 100px;
  max-width: 150px;
  padding: 4px 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 0.9em;
}

.service-date-input {
  width: 100%;
  min-width: 0;
  max-width: none;
  justify-self: end;
}

/* Pet Info Row */
.pet-info-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.pet-field {
  display: flex;
  align-items: center;
  gap: 4px;
}

.pet-field label {
  font-size: 0.85em;
  white-space: nowrap;
}

.pet-count-input {
  width: 30px;
  padding: 3px 2px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 0.85em;
  text-align: center;
  height: 24px;
  box-sizing: border-box;
  -moz-appearance: textfield;
}

.pet-count-input::-webkit-outer-spin-button,
.pet-count-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.pet-names-field {
  flex: 1;
}

.pet-names-input {
  flex: 1;
  width: 100%;
  padding: 3px 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 0.85em;
  height: 24px;
  box-sizing: border-box;
}

.pet-temperament-btn {
  width: 24px;
  height: 24px;
  min-width: 24px;
  border: 1px solid #ccc;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.8em;
  padding: 0;
  box-sizing: border-box;
  margin-top: -5px;
  transition: background-color 0.2s;
}

.pet-temperament-btn:hover {
  opacity: 0.85;
}

.edit-dialog-footer,
.dialog-buttons {
  text-align: center;
}

.edit-dialog-footer button,
.dialog-buttons button {
  background-color: #a080a8;
  color: #fff;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  margin: 0 5px;
}

.edit-dialog-footer button:hover,
.dialog-buttons button:hover {
  background-color: #a080a8;
}

.dialog-buttons {
  background-color: #dc3545;
}

.note-textarea {
  width: calc(100% - 20px);
  height: 200px;
  margin-bottom: 15px;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: lightyellow;
}

/* link lease dialog section */

.faded {
  color: #ddd;
}

.confirmation-popup {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1001;
}
.confirmation-popup-title {
  margin: -20px;
  margin-bottom: 10px;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  background: #333;
  color: #eee;
  padding: 8px;
}

.confirmation-popup-content {
  position: absolute;
  top: 300px;
  left: 150px;
  width: 300px;
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 9px 13px 7px 4px rgb(0 0 0 / 22%);
  z-index: 2000;
  user-select: none;
}

.confirmation-popup-buttons button {
  margin: 10px;
  padding: 10px 20px;
  background-color: #a080a8;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.confirmation-popup-buttons button:hover {
  background-color: #776279 !important;
}

/* tenant search dialog section */
.edit-dialog-body ul {
  margin-left: -36px;
  margin-top: 2px;
  list-style-type: none;
}

.edit-dialog-body li {
  padding: 5px;
}
.edit-dialog-body label {
  font-size: 0.9em;
  margin-top: 4px;
}

.edit-dialog-body li:nth-child(even) {
  background-color: #eee;
}

.edit-dialog-body li:nth-child(odd) {
  background-color: #ddd;
}

.edit-dialog-body li:hover {
  background-color: #907793;
}

/* Above LeaseSearch fixed dialogs (edit-dialog z-index 1001, etc.) */
.person-editor-host {
  position: fixed;
  inset: 0;
  z-index: 10050;
  pointer-events: none;
}
.person-editor-host > * {
  pointer-events: auto;
}

.tenant-search-dialog {
  height: 575px;
  position: absolute;
  user-select: none;
}

.tenant-search-dialog .edit-dialog {
  border: 1pt solid #888;
}

.tenant-search-dialog .search-results {
  height: 320px;
  border: 1pt solid #eee;
  padding: 3px;
  width: 451px;
  overflow-y: auto;
  border-radius: 4px;
  margin-left: 12px;
  margin-top: 5px;
  font-size: 10pt;
}

.tentant-search-dialog .edit-dialog-content {
  height: 95%;
  padding: 5px;
  border: 1 pt solid #eee;
}

.tenant-search-dialog .edit-dialog-footer {
  margin-top: 15px;
}

.tenant-search-dialog .edit-dialog-footer button {
  width: 140px;
}

.selected-row {
  background-color: #907793 !important;
  color: #fff;
}

.multi-selected-row {
  background-color: #c5a3c9 !important;
  color: #fff;
}

.selected-total {
  font-size: 0.85em;
  font-weight: 600;
  color: #555;
  white-space: nowrap;
}

tr {
  cursor: pointer;
}

/* for new person dialog */
.add-new-person {
  margin-top: 2px;
}

.scrollbar-wrapper {
  height: 395px;
  overflow-y: hidden;
  padding-right: 10px;
  overflow-x: hidden;
  border: 1pt solid #ddd;
  /* padding: 10px; */
  width: 98%;
  margin-top: 3px;
  margin-left: 3px;
  border-radius: 4px;
}

.form-row {
  display: flex;
  flex-wrap: wrap;
}

.form-group {
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;
}

.half-width {
  width: 50%;
}

.full-width {
  width: 100%;
}

.button-row {
  margin-top: 0;
}

.add-new-person input {
  padding: 0.5rem;
  margin-bottom: 0.5rem;
  width: 87% !important;
}

.add-new-person .wide-input {
  width: 90%;
}

.add-new-person .tall-input {
  height: 40px;
}

.button-group {
  display: flex;
  justify-content: space-between;
  margin-top: 0.5rem;
}

.button-group button {
  width: 48%;
}

.add-new-person button {
  display: block;
  padding: 1rem;
  background-color: #a080a8;
  color: white;
  border: none;
  cursor: pointer;
  height: 25px;
  width: 100px;
}

.add-new-person textarea {
  width: 165px;
  height: 60px;
  margin-top: 5px;
  font-family: monospace;
  font-size: 1.1em;
  color: #000000 !important;
}

.add-new-person #fullName {
  width: 93% !important;
  margin-top: 10px;
}

.add-new-person .button-row button {
  width: 140px;
  padding: 5px;
  height: 38px;
  border-radius: 4px;
  background-color: #a080a8;
  border: 1pt solid #888;
}

.add-new-person .button-row {
  justify-content: center;
  gap: 26px;
  text-align: center;
}

.add-new-person .error-msg {
  border: 1pt solid #b3b3b3;
  color: #f14242;
  width: 368px;
  height: 17px;
  border-radius: 4px;
  padding: 5px;
}

.new-person-heading {
  margin-left: 15px;
  margin-bottom: 5px;
  font-weight: bold;
  font-size: 1.2em;
}

.footer-message {
  transition: background-color 0.5s ease; /* Smooth transition for the background fade */
  position: fixed; /* Position it absolutely relative to the parent container */
  border-radius: 4px;
  border: none;
  bottom: 0; /* Align it to the bottom of the parent container */
  left: 0;
  width: 100vw; /* Make it span the full width of the parent container */
  height: 20px; /* Set the desired height */
  text-align: left; /* Optional: Center text horizontally */
  line-height: 20px; /* Optional: Center text vertically within the 20px height */
  font-size: 0.8em;
  padding: 3px;
  padding-left: 6px;
  z-index: 1000; /* Ensure the footer stays on top of other elements */
}

/* Temporary background class to be applied and then removed */
.highlight-footer-message {
  background-color: #ddd;
  border: 1pt solid #ddd;
}

/* ledger classes */

.ledger-wrapper {
  border: 1pt solid #ccc;
  margin-top: 20px;
  padding: 10px;
  width: 830px;
  height: 770px;
}

.ledger-body .ledger-summary {
  background-color: #f2f2f2;
  padding: 10px;
  border-bottom: 2px solid #000;
  font-size: 0.9em;
}

.ledger-body .summary-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 5px;
}

.ledger-body .summary-item:first-child {
  flex: 2; /* Make the first item wider */
}

.ledger-body .summary-item:not(:first-child) {
  flex: 1; /* Make the remaining items equal size */
  display: flex;
  align-items: center;
  min-width: 0; /* Ensure flexibility in resizing */
}

.ledger-body .summary-row.full-width {
  width: 100%;
}

.ledger-body .summary-item {
  flex: 1;
  display: flex;
  align-items: center;
  min-width: 0;
}

.ledger-body .summary-item label {
  font-weight: normal; /* Regular font weight for labels */
  margin-right: 5px;
}

.ledger-body .summary-row.full-width .summary-item {
  width: 100%; /* Full width for the second row */
}

.ledger-body .summary-item strong,
.ledger-body .summary-item a {
  white-space: nowrap; /* Prevent text wrapping */
}

.transaction-table-outer {
  height: 600px;
  overflow: hidden;
}

.transaction-table-inner {
  height: 595px;
  overflow-y: scroll;
  width: 848px;
}

.ledger-body .transaction-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
  font-size: 13px; /* Adjust font size for readability */
  user-select: text !important;
}

.ledger-body .transaction-table th,
.ledger-body .transaction-table td {
  border: 1px solid #aaa;
  padding: 5px;
}

.ledger-body .transaction-table th {
  background-color: #bbbbbb;
  text-align: left; /* Default alignment for headers */
  vertical-align: top;
}

.ledger-body .transaction-table td.left-align,
.ledger-body .transaction-table th.left-align {
  text-align: left;
}

.ledger-body .transaction-table td.right-align,
.ledger-body .transaction-table th.right-align {
  text-align: right;
}

.ledger-body .transaction-table td.center-align,
.ledger-body .transaction-table th.center-align {
  text-align: center;
}

.ledger-body .transaction-table td.wide-col {
  width: 130px;
}

.ledger-body .transaction-table tbody tr:nth-child(even) {
  background-color: #ccc;
}

.ledger-body .transaction-table tbody tr:nth-child(odd) {
  background-color: #ffffff;
}

.ledger-body .transaction-table tbody tr.highlight td {
  font-style: italic;
  color: red;
}

.ledger-body .footer-summary {
  text-align: right;
  font-weight: bold;
}

.ledger-body .wide-col {
  min-width: 115px;
  max-width: 115px;
}

.ledger-body tfoot td {
  background-color: #f2f2f2;
}

.ledger-body {
  margin-top: 5px;
  font-size: 0.9em;
  margin-bottom: 20px;
}

#ledgerBody {
  max-height: 705px;
  overflow-y: hidden;
}

.no-start-date {
  margin-top: 20px;
}

.ledger-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 10px;
}

.ledger-title {
  font-size: 1em;
  font-weight: bold;
  margin-top: 5px;
  font-size: 1.3em;
}

.button-group {
  display: flex;
  gap: 10px; /* Space between buttons */
}

.button-group button {
  padding: 6px 12px;
  background-color: #7a7b7c;
  border: none;
  color: white;
  cursor: pointer;
  border-radius: 4px;
}

.button-group button:hover {
  background-color: #0056b3;
}

/* Removed empty CSS rules */
#ledgerPopup {
  position: fixed; /* Fixes the popup relative to the window */
  top: 25%; /* Positions 25% from the top of the window */
  left: 30px; /* Positions 30px from the left edge of the window */
  width: 400px; /* Width of the popup */
  z-index: 9999; /* Ensures the popup is layered on top of other elements */
  background-color: white; /* Popup background color */
  border: 2px solid #ccc; /* Adds a border */
  padding: 20px; /* Padding inside the popup */
  box-shadow: 0px 0px 15px rgba(0, 0, 0, 0.5); /* Adds shadow to the popup */
  border-radius: 5px; /* Rounds the corners */
}

#ledgerPopup .button-row {
  margin-top: 30px;
}

#ledgerPopup .popup-header {
  /* font-size: 1.3em; */
  margin: -22px;
  padding: 8px;
  background: #333;
  color: #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  padding-left: 12px;
}

.close-button {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.9em;
  position: absolute;
  right: 10px;
}

#ledgerPopup .popup-body {
  display: flex;
  flex-direction: column;
  gap: 10px; /* Space between form elements */
}

#ledgerPopup .popup-footer {
  display: flex;
  justify-content: space-between; /* Space out the buttons */
}

#ledgerPopup button:focus {
  border: 1pt solid #c9a3d3;
}

#ledgerPopup button {
  background: #a080a8;
  color: #fff;
  border: 1 pt solid #ddd;
  cursor: pointer;
  border-radius: 4px; /* Rounded corners for buttons */
  border-color: #aaa;
}

#ledgerPopup .popup-button {
  margin: 0 25px;
  max-width: 75px;
}

#ledgerPopup label {
  font-size: 0.9em;
}

#ledgerPopup button:hover {
  background-color: #3a236a; /* Darker shade on hover */
}

.form {
  margin-left: 30px;
  margin-top: 35px;
}

#ledgerPopup .form input[type='text'],
.form input[type='number'],
.form input[type='date'],
.form select {
  width: 65%; /* Make all form inputs fill the available width */
  box-sizing: border-box; /* Ensure padding doesn't increase the width */
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.overlay {
  position: fixed; /* Covers the whole window */
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5); /* Semi-transparent background */
  z-index: 9998; /* Below the popup but above everything else */
}

.popup-footer {
  display: flex;
  justify-content: space-between;
  width: 100%;
}

.action-buttons {
  display: flex;
  justify-content: space-between;
  gap: 15px; /* Adds equal space between the buttons */
}

.action-buttons button {
  flex-grow: 1; /* Ensures buttons are evenly sized */
}

.button-row {
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
}

.popup-button {
  flex-grow: 1;
  text-align: center;
  padding: 10px;
  background-color: #4b2e83; /* Or any color you choose */
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  margin: 0 5px; /* Adds spacing between buttons */
}

.popup-button i {
  display: block; /* Stack icon and text vertically */
  font-size: 1.5em;
}

.popup-button div {
  margin-top: 5px;
}

.active {
  background-color: #6a45a2 !important; /* Slightly different shade when active */
}

.popup-footer {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
}

.popup-footer button {
  flex-grow: 1;
  padding: 10px;
  margin: 0 5px;
}

#ledgerPopup .form-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
}

#ledgerPopup .form-row label {
  width: 30%;
  text-align: left;
  margin-top: 12px;
}

#ledgerPopup .form-row input,
.form-row select {
  width: 65%;
}

.new-ledger-entry-error {
  margin-top: 20px;
  margin-left: 36px;
  color: rgb(215 72 72);
}

.new-record {
  background: purple;
}

/* for email popup */
.email-compose-wrapper {
  position: fixed;
  top: 100px;
  left: 200px;
  width: 600px;
  height: auto;
  background-color: white;
  padding: 10px;
  box-shadow: 7px 7px 10px rgba(0, 0, 0, 0.25);
  border-radius: 8px;
  z-index: 1000;
}

.email-compose-wrapper-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.25);
  z-index: 999;
}

/* Close button in top right */
.email-compose-close-btn {
  position: absolute;
  top: -9px;
  right: 10px;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: white;
}

/* Header style */
.email-compose-header {
  text-align: left;
  background: #333;
  color: #eee;
  padding: 10px;
  margin: -10px;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  margin-bottom: 5px;
  cursor: move;
  user-select: none;
}

/* Flexbox row for To: and Subject: */
.email-compose-row {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.email-compose-row label {
  width: 70px;
  flex-shrink: 0;
  margin-right: 10px;
  font-weight: bold;
}

.email-compose-row input {
  flex-grow: 1;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.preview-button {
  height: 30px;
  min-width: auto;
  margin-top: 4px;
  margin-left: 8px;
  margin-right: 0;
  padding: 6px 10px;
  background-color: #9b7ba8;
  color: white;
  border: 1px solid #7a5f85;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.78em;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.preview-button:last-of-type {
  margin-right: 20px;
}

.preview-button:hover {
  background-color: #7a5f85;
}

.dan-to-button {
  background-color: #7d6882;
}

/* Form Inputs */
.email-compose-body label {
  display: block;
  font-weight: bold;
  margin-top: 10px;
}

.email-compose-body input[type='text'],
.email-compose-body textarea {
  padding: 8px;
  margin-top: 5px;
  border: 1px solid #e2e2e2;
  border-radius: 4px;
}

.email-compose-body input[type='text'] {
  margin-right: 20px;
  width: 610px;
}

.email-compose-body textarea {
  width: 94%;
}

#emailBodyTop {
  height: 120px;
}

/* To: input field is read-only */
.email-compose-body input[readonly] {
  background-color: #f9f9f9;
  color: #555;
  cursor: not-allowed;
}

/* Footer with buttons */
.email-compose-footer {
  text-align: right;
  margin-right: 20px;
  margin-top: 5px;
}

.email-compose-btn-save {
  background-color: #9b7ba8;
  color: white;
  padding: 10px 15px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
}

.email-compose-btn-save:hover {
  background-color: #4b317c;
}

.email-compose-btn-close {
  background-color: #ccc;
  color: black;
  padding: 10px 15px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  margin-left: 10px;
}

.email-compose-btn-close:hover {
  background-color: #aaa;
}

.help-popup {
  font-size: 0.9em;
  -webkit-user-select: none;
  -moz-user-select: none;
  user-select: none;
  position: absolute;
  overflow: auto;
  width: 30%;
  height: 30%;
  top: 25%;
  left: 25%;
  background: #f1f0c4;
  border: 1pt solid #3f3f3f;
  padding: 10px;
  border-radius: 4px;
  box-shadow: 8px 7px 10px 4px rgb(0 0 0 / 25%);
}

.close-help {
  position: absolute;
  bottom: 6px;
  width: 95%;
  text-align: center;
  font-style: italic;
}

.validation-message {
  height: 30px;
  border: 1pt solid #ddd;
  padding: 4px;
  margin-bottom: 10px;
  color: darkred;
}

/* check viewer */
.check-viewer {
  position: absolute;
  top: 20%;
  left: 5%;
  width: 790px;
  height: 460px;
  border: 1px solid rgb(145, 146, 146);
  overflow: hidden;
  display: flex;
  flex-direction: column; /* Ensures the header is above the iframe */
  background-color: #fff;
  border-radius: 5px;
}
/* ledger viewer */
.ledger-viewer {
  position: absolute;
  top: 10%;
  left: 5%;
  width: 790px;
  height: 764px;
  border: 1px solid rgb(145, 146, 146);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background-color: #fff;
  border-radius: 5px;
  box-shadow: 5px 6px 8px 5px rgba(0, 0, 0, 0.4);
}

/* Title bar styles */
.check-viewer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #333;
  color: #eee;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
}

.check-viewer-title {
  font-weight: bold;
  text-align: center;
  flex-grow: 1;
  cursor: move;
}

.check-viewer-close-button {
  background: transparent;
  color: #eee;
  border: none;
  font-size: 16px;
  cursor: pointer;
  position: relative;
  right: 20px;
  top: -4px;
}

.check-viewer-close-button:hover {
  color: #ff6666; /* Optional: change color on hover */
}

/* iframe styles */
.check-viewer iframe {
  width: 100%;
  height: 100%;
  border: none;
}

:deep(.highlighted-payment) {
  background-color: #c4a6c7 !important;
}

.compose-preview {
  border-radius: 5px;
  margin-top: 10px;
  border: 1px solid #ddd;
  padding: 10px;
  min-height: 250px;
  font-size: 0.8em;
  width: 93.5%;
  overflow-y: auto;
  max-height: 375px;
}

.preview-label {
  margin-top: 5px;
  margin-left: 5px;
  font-size: 0.8em;
  font-style: italic;
}

.markdown-help {
  margin-top: 8px;
  margin-left: 8px;
  font-size: 0.9em;
}

/* ledger list styles */
.ledger-list-popup {
  position: absolute;
  top: 20%;
  left: 20%;
  background-color: #ffffffd1;
  border: 1px solid #878787;
  border-radius: 8px;
  padding: 20px;
  width: 650px;
  z-index: 1000;
  box-shadow: 5px 6px 8px 4px rgba(0, 0, 0, 0.2);
}

.ledger-list-title {
  margin-top: -21px;
  margin-left: -21px;
  margin-right: -21px;
  margin-bottom: 2px;
  color: #eee;
  background-color: #333;
  border-top-left-radius: 6px;
  border-top-right-radius: 6px;
  padding: 8px;
  border-top: 1pt solid #aaa;
  cursor: move;
}

.ledger-list-popup .ll-button-row {
  text-align: right;
}

.ledger-list-popup button {
  border: 1pt solid #888;
  border-radius: 5px;
  margin-left: 4px;
  background: #8b8b8b00;
}

.ledger-list {
  list-style-type: none;
  padding: 0;
  margin: 0;
  font-size: 0.85em;
}

.ll-list-wrapper {
  height: 350px;
  overflow-y: auto;
  font-size: 0.9em;
  padding-top: 5px;
}

.ledger-list li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 2px;
  padding-left: 4px;
  height: 28px;
  cursor: pointer;
}

.ledger-list li:hover {
  background: #907793;
  color: #fff;
}

.ledger-list li:nth-child(even) {
  /* background: #dddddd00; */
  border-bottom: 1px solid #c2c1c1;
}

.ledger-list li span {
  flex: 1;
  text-align: left;
}

.ledger-list li span:first-child {
  flex: 3; /* Property/title column - wider */
}

.ledger-list li span:nth-child(2) {
  flex: 2; /* Date column - medium width */
}

.ledger-list li span:nth-child(3) {
  flex: 1; /* Balance override amount column - narrower */
  text-align: right;
}

.ledger-list button {
  background: none;
  border: none;
  cursor: pointer;
  color: #555;
  margin-bottom: 8px;
}

.ledger-list button i {
  font-size: 1.2em;
}

button {
  margin-top: 10px;
  padding: 5px 10px;
  cursor: pointer;
}

.draggable-icon {
  cursor: pointer;
  color: #888;
  transition: color 0.3s;
  position: absolute;
  right: 10px;
}

.draggable-icon.active {
  color: #4a90e2;
}
.edit-lease-title,
.edit-dialog-title {
  margin-top: -20px;
  font-size: 1.4em;
  margin-left: -10px;
  margin-right: -10px;
  background: #333;
  padding: 10px;
  color: #ddd;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
}

.popup-header .close-button {
  position: relative;
  top: -7px;
  right: 4px;
  background: none;
  border: none;
  padding: 0;
}
.ledger-howto {
  margin-top: 10px;
  font-size: 0.75em;
  font-style: italic;
  padding: 5px;
  background: white;
  color: #111;
}

.temp-data.container {
  width: 400px;
  margin: 20px auto;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-left: 10px;
  top: 270px;
  position: relative;
  height: 500px;
  font-size: 0.75em;
  overflow-y: hidden;
  overflow-x: hidden;
}

.temp-table-wrapper {
  height: 380px;
  width: 475px;
  overflow-y: scroll;
  overflow-x: hidden;
}

.temp-data .temp-form {
  display: flex;
  justify-content: space-between;
  height: 35px;
  padding: 5px;
}

.temp-data .temp-form input {
  flex: 1;
  margin-right: 10px;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.temp-data .temp-form button {
  padding: 5px;
  border: none;
  background: #a080a8;
  color: white;
  border-radius: 4px;
  cursor: pointer;
  height: 26px;
  /* margin-bottom: -9px; */
}

.temp-data .temp-form button:hover {
  background: #0056b3;
}

.temp-data .temp-table {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.temp-data .temp-table-wrapper {
  user-select: text !important;
}

.temp-data .temp-row {
  display: flex;
  width: 100%;
  border-bottom: 1px solid #ddd;
  padding: 1px 1px;
  cursor: pointer;
}

.temp-data .temp-row:nth-child(odd) {
  background: #fff;
}

.temp-data .temp-row:nth-child(even) {
  background: #eee;
}

.temp-header {
  background: #666 !important;
  color: #eee;
  min-width: 107%;
}

.temp-data .temp-cell {
  padding: 2px 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.temp-data .temp-cell:nth-child(1) {
  flex: 0.5;
}

.temp-data .temp-cell:nth-child(3),
.temp-data .temp-cell:nth-child(4) {
  text-align: right;
}

.temp-data .highlight {
  background-color: #c4a6c7 !important;
}
.temp-data-title {
  background: #333;
  color: #eee;
  margin: -11px;
  margin-bottom: 10px;
  padding: 10px;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
}

.tip {
  margin-top: 5px;
}

.confirmation-popup {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1001;
}

.confirmation-popup-content {
  position: absolute;
  top: 300px;
  left: 150px;
  width: 300px;
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 9px 13px 7px 4px rgb(0 0 0 / 22%);
  z-index: 2000;
  user-select: none;
}

.confirmation-popup-buttons button {
  margin: 10px;
  padding: 10px 20px;
  background-color: #a080a8;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.confirmation-popup-buttons button:hover {
  background-color: #0056b3;
}
.confirmation-message {
  width: 290px; /* Set a fixed width */
  white-space: nowrap; /* Prevent text from wrapping to the next line */
  overflow: hidden; /* Hide the excess content */
  text-overflow: ellipsis; /* Display ellipsis (...) at the end of the block */
  padding: 5px; /* Optional: add some padding */
}

#pdf-frame {
  width: 100%;
  height: 730px;
}

.spinner {
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #fff;
  animation: spin 1s infinite linear;
  margin-left: 0px;
  vertical-align: middle;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.demand-balance-override {
  border: 1pt solid #ddd;
  border-radius: 5px;
  width: 180px;
  margin-right: 20px;
  margin-left: 10px;
  font-size: 0.9em;
  outline: none;
}
.quick-action {
  border-radius: 4px;
  border: 1px solid #ddd;
  padding: 6px;
  width: 20%;
  margin-right: 5px;
}
</style>
